import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack5";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from "@material-ui/core";
import {
  Send as SendIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Layers as LayersIcon,
  Add as AddIcon
} from "@material-ui/icons";
import { PATH_DASHBOARD } from "../../../routes/paths";
import type {
  RealEstateContact,
  ProcessedListingData,
  User,
  CustomerType
} from "../../../@types/sharedSchema";
import { getContactCustomerTypes } from "../../../@types/sharedSchema";
import OrgUsersPicker from "../common/OrgUsersPicker";
import useContactHandler from "hooks/crm/useContactHandler";
import CrmMemberAvatar from "../crm/org/CrmMemberAvatar";
import { db } from "../../../firebaseConfig";
import {
  listTemplates,
  listBundles,
  getCachedTemplates,
  getCachedBundles,
  createDocument,
  getDocument,
  updateDocument,
  sendDocument,
  getUserTemplateCounter,
  listUserTemplateCounters,
  getBundle,
  getTemplate,
  getSignatureDefaultTemplates,
  getCachedSignatureDefaultTemplates,
  getCachedTemplateById,
  sendBundle as sendBundleApi,
  editSentDocument
} from "../../../services/signatures";
import { fetchListingById } from "../../../services/signaturesListings";
import {
  SignatureDocument,
  SignatureTemplate,
  SignatureTemplateBundle,
  SignatureTemplateField,
  SignaturePrefillSource,
  USER_INFO_BLOCK_LINE_KEYS,
  UserInfoBlockLineKey,
  DEFAULT_PRICE_PERCENT_SUFFIX,
  PRICE_ONE_MONTH_RENT_LABEL,
  ListingsBlockListingFilter,
  DEFAULT_ADDITIONAL_TERMS_TITLE,
  effectiveFillMode,
  isSignatureFieldRequiredForSigner
} from "../../../@types/signatures";
import {
  REST_schema,
  SIGNATURE_EXCLUSIVITY_ON_BY_DEFAULT,
  SIGNATURE_EXCLUSIVITY_TEMPLATE_KEYS,
  SIGNATURE_QUESTIONNAIRE_ONLY_ROLES,
  SIGNATURE_QUESTIONNAIRE_ONLY_TEMPLATE_KEY,
  SignatureDefaultTemplateKey,
  SignatureDefaultTemplates,
  SignatureSendFormSnapshot,
  isQuestionnaireOnlyDocument
} from "../../../@types/sharedSchema";
import { getFieldFriendlyName, formatDate } from "../../../services/signatureFormatters";
import {
  buildEffectiveListingContext,
  questionnairePropertyAddress,
  type ListingOverride
} from "./signatureListingContext";
import { QuestionnaireRidesHere } from "./QuestionnaireStepLabel";
import { templateCarriesQuestionnaire } from "../../../utils/signatureQuestionnaireStep";
import {
  QuestionnaireAddressAlert,
  SellerQuestionnaireNotice,
  isQuestionnaireAddressMissing,
  questionnaireCarrierTitlesOf
} from "./SellerQuestionnaireNotice";
import { pickerListingFilter } from "./listingPickerFilter";
import { roleDocumentsBlockKey, roleDocumentsState, roleTemplatePlan, type RoleIssue } from "./roleDocuments";
import { RoleDocumentsListPlaceholder, RoleDocumentsNote } from "./RoleDocumentsNote";
import {
  questionnaireLabelContext,
  questionnaireListedForAtSend
} from "../../../utils/signatureQuestionnaireName";
import {
  LISTING_ADDRESS_PART_KEYS,
  type ListingAddressParts
} from "./listingAddressRequirements";
import ListingDetailsSection, {
  addressPartLabel,
  getListingsCompleteness,
  listingShortLabel
} from "./ListingDetailsSection";
import {
  isValidPhone,
  validateFieldValue,
  type FieldValidationError
} from "../../../services/signatureValidation";
import { getFullName } from "../../../utils/Haim/utils";
import {
  renderHtmlForSend,
  renderAddressesAddendumHtml,
  SendRenderContext
} from "../../../services/signaturesHtmlSend";
import type { HtmlToPdfResult } from "../../../services/signaturesHtmlToPdf";
import {
  compileDocuments,
  createPrepareAhead,
  enterCompileStep,
  enterSaveStep,
  startPreparation,
  PREPARE_AHEAD_DELAY_MS,
  SIGNATURE_PREPARE_AHEAD_ENABLED,
  type CompileItem,
  type PreparationKind,
  type PreparationProgress
} from "../../../services/signaturePreparation";
import useFirestore from "../../../hooks/useFirestore";
import useContactWrites from "../../../hooks/contacts/useContactWrites";
import useContactByPhone from "../../../hooks/contacts/useContactByPhone";
import { NewContactPicker } from "../listingsMatch/NewContactPicker";
import { NewListingPicker } from "./NewListingPicker";
import { EditContactDialog } from "../contacts/modal/EditContactDialog";
import { NewListingDialog } from "../listings/modal/dialog/NewListingDialog";
import { usePropertyTypes } from "../../../hooks/usePropertyTypes";
import FavoriteListingsModalButton from "./FavoriteListingsModalButton";
import AdditionalTermsInput from "./AdditionalTermsInput";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import StackedAlert from "../common/StackedAlert";
import TemplateOriginBadge from "./TemplateOriginBadge";
import TemplateNameTag from "./TemplateNameTag";
import type { PreparedSignatureResult } from "./PreparedSignatureDialog";
import SignaturePreparationProgress from "./SignaturePreparationProgress";

/**
 * "New signature" flow body. Embedded inside GlobalNewSignatureDialog.
 *
 * HTML migration: drops PDF stamping + reflow; renders the live preview by
 * passing the saved template HTML through `renderHtmlForSend` with the
 * realtor / contact / listing context. On send, ships the resolved HTML to
 * the backend (no more multipart upload).
 */

/**
 * Roles offered by the quick-pick row. `sellerExclusive` / `landlordExclusive`
 * are NOT standalone quick-pick values — each is its role (seller / landlord)
 * with the exclusivity toggle on. Nor is `ownerQuestionnaire`: it is a seller
 * or a landlord with "שאלון בלבד" ticked.
 */
export type SignatureQuickPickRole = Exclude<
  SignatureDefaultTemplateKey,
  "sellerExclusive" | "landlordExclusive" | "ownerQuestionnaire"
>;

export const SIGNATURE_QUICK_PICK_ROLES: SignatureQuickPickRole[] = [
  "buyer",
  "seller",
  "renter",
  "landlord",
  "brokerCooperation"
];

/**
 * Default names a dynamic-fee (`price`) field may carry. When the field's
 * stored name is one of these — i.e. the realtor never gave it a custom name —
 * the send form shows a simple localized "עמלה" label. The field TYPE is
 * "עמלה דינמית", but during signing we want just "עמלה". Covers the pre-rename
 * "מחיר" defaults too, so it works without a data migration. A custom name is
 * always preserved.
 */
const DEFAULT_PRICE_FIELD_NAMES = new Set([
  "מחיר דינמי",
  "מחיר",
  "עמלה דינמית",
  "Dynamic price",
  "Dynamic fee",
  "Price"
]);

/**
 * Sentinel id for the CLIENT-ONLY bundle a role + exclusivity quick-pick
 * builds (the role's template + its exclusivity template, sent together). It
 * never reaches the server as a bundleId — the send path swaps it for the
 * `adHoc` payload, and the server mints the real grouping id. Nothing is
 * written to the user's bundle list.
 */
const ADHOC_BUNDLE_ID = "__adhoc__";

/**
 * Contact customer type (Hebrew, from the CRM taxonomy) → quick-pick signing
 * role. Only the four signing scenarios map; other types (מתווך, עו"ד, שמאי)
 * have no default-template scenario.
 */
const CUSTOMER_TYPE_TO_QUICK_PICK_ROLE: Partial<Record<CustomerType, SignatureQuickPickRole>> = {
  קונה: "buyer",
  מוכר: "seller",
  שוכר: "renter",
  משכיר: "landlord"
};

/**
 * Derives the quick-pick role for a contact from its customer types (first
 * mappable type wins, in the contact's own order). Used by "החתמת לקוח"
 * entry points to pre-select the right default template. Null when the
 * contact has no mappable type.
 */
export const quickPickRoleForContact = (
  contact: Pick<RealEstateContact, "customerType" | "customerTypes"> | null | undefined
): SignatureQuickPickRole | null => {
  for (const type of getContactCustomerTypes(contact)) {
    const role = CUSTOMER_TYPE_TO_QUICK_PICK_ROLE[type];
    if (role) return role;
  }
  return null;
};

export type NewSignatureFormProps = {
  initialContactId?: string | null;
  initialTemplateId?: string | null;
  /**
   * Pre-selects a signing scenario in the quick-pick row (e.g. from the
   * contact menu, derived from the contact's customer types). The matching
   * default template is auto-selected once the defaults load. Ignored when
   * `initialTemplateId` / `initialBundleId` is set (explicit selection wins).
   */
  initialRole?: SignatureQuickPickRole | null;
  /**
   * Opening state of the exclusivity checkbox for `initialRole` (seller /
   * landlord). Null leaves the role's own default: on for seller, off for
   * landlord. Set by the WhatsApp AI's deep link.
   */
  initialExclusivity?: boolean | null;
  /**
   * Opens a seller / landlord `initialRole` with "שאלון בלבד" ticked: the statutory owner
   * questionnaire on its own (the `ownerQuestionnaire` slot) instead of the role's order. Set by
   * the property card's "שליחת שאלון לבעל הנכס". Ignored for any other role.
   */
  initialQuestionnaireOnly?: boolean | null;
  /**
   * If set, the form opens in **bundle mode**: it loads the bundle, fetches
   * every template inside, and renders a single set of inputs unioned across
   * the bundle. Send creates N documents in one call. When both `initialBundleId`
   * and `initialTemplateId` are set, bundle wins (single-template selection is
   * ignored in bundle mode).
   */
  initialBundleId?: string | null;
  /** Called once the PDFs and signing links are ready; no messages have been sent. */
  onSent: (result: PreparedSignatureResult) => void;
  onBusyChange?: (busy: boolean) => void;
  /**
   * Pre-selects listings in the send form's picker (deep link from the
   * WhatsApp AI's prepare_signature_document, or any caller that already knows
   * which property the document is about). Applied ONCE, only after the
   * resolved template turns the listings picker on — templates without a
   * listings block simply ignore it. Ignored in edit mode (the document's own
   * listingIds win).
   */
  initialListingIds?: string[] | null;
  /**
   * EDIT MODE: the SENT (but not yet signed) document being edited. The form
   * prefills from it (template + contact via the matching `initial*` props the
   * dialog passes alongside; listings / field values / notes / OTP / terms
   * from the document itself), locks template selection and the document
   * number, and submits through the edit-resend endpoint instead of
   * create+send — the signers then get an SMS that the document was edited.
   */
  editDocument?: SignatureDocument | null;
};

/** Fields the realtor types into the form before sending — those with
 *  fillMode=prefilled but no prefillSource (typed-by-sender). */
const isSenderTyped = (f: SignatureTemplateField): boolean =>
  effectiveFillMode(f) === "prefilled" && (!f.prefillSource || f.prefillSource === "none");

/**
 * Contact sources a SIGNER-filled field can be optionally pre-seeded from by
 * the realtor before sending. The seed rides `signers[].fieldValues` (keyed
 * by the field's tag name, `linkGroupId || name`), which the public signing
 * payload echoes back as `prefilledValues` — so the signer sees the value
 * pre-filled in the interactive input and can freely change it before
 * signing. The signer-branch renderer never bakes a value into the PDF, so
 * the seed is purely an input default.
 */
const SIGNER_PREFILL_CONTACT_SOURCES = [
  "contact.name",
  "contact.email",
  "contact.id_number",
  "contact.address"
] as const;
type SignerPrefillContactSource = typeof SIGNER_PREFILL_CONTACT_SOURCES[number];

/**
 * Resolve the contact semantics of a signer-filled field. A fillMode switch
 * to 'signer' clears `prefillSource` but keeps `labelKey` — so detection
 * reads `prefillSource ?? labelKey`. Null when the field has no contact
 * semantics (or none we can seed).
 */
const resolveSignerPrefillSource = (
  f: SignatureTemplateField
): SignerPrefillContactSource | null => {
  const src = f.prefillSource ?? f.labelKey;
  return src && (SIGNER_PREFILL_CONTACT_SOURCES as readonly string[]).includes(src)
    ? (src as SignerPrefillContactSource)
    : null;
};

/** The picked contact's value for one signer-prefill source ("" when the
 *  contact lacks it or no contact is picked). */
const contactValueForSignerPrefill = (
  c: RealEstateContact | null,
  source: SignerPrefillContactSource
): string => {
  if (!c) return "";
  switch (source) {
    case "contact.name":
      return getFullName(c.callerFirstName, c.callerLastName);
    case "contact.email":
      return c.mail || "";
    case "contact.id_number":
      return c.tehudatZehut || "";
    case "contact.address":
      return c.address || "";
    default:
      return "";
  }
};

/** Label shown for a signer-prefill input / validation row — the signer-facing
 *  label when set, else the source's friendly name, else the raw field name. */
const signerPrefillLabel = (f: SignatureTemplateField): string =>
  f.signerLabel?.trim() ||
  getFieldFriendlyName(f.prefillSource ?? f.labelKey) ||
  f.name;

/**
 * Build a synthetic "view template" that unions fields/listingsBlocks/
 * signerRoles across every template in a bundle, used by the form's inputs
 * and preview. Fields dedupe by `(name, signerRoleId)` — same key as the
 * server-side bundle field projection. The first template provides the
 * canonical `id`, `name`, `content`, etc. — so any existing memo that reads
 * `selectedTemplate.id` (counter lookup, etc.) still works in bundle mode.
 *
 * At send time the form does NOT use this merged template — each real
 * template gets its own render + PDF + perDocument[] entry against its OWN
 * fields. Values typed in the form are projected back per-template by name
 * match.
 */
const buildMergedBundleTemplate = (templates: SignatureTemplate[]): SignatureTemplate => {
  if (templates.length === 0) throw new Error("buildMergedBundleTemplate requires >= 1 template");
  if (templates.length === 1) return templates[0];
  const first = templates[0];
  const seenFieldKeys = new Set<string>();
  const fields: SignatureTemplateField[] = [];
  for (const tp of templates) {
    for (const f of tp.fields || []) {
      const key = `${f.name}:${f.signerRoleId}`;
      if (seenFieldKeys.has(key)) continue;
      seenFieldKeys.add(key);
      fields.push(f);
    }
  }
  const seenRoleIds = new Set<string>();
  const signerRoles: SignatureTemplate["signerRoles"] = [];
  for (const tp of templates) {
    for (const r of tp.signerRoles || []) {
      if (seenRoleIds.has(r.id)) continue;
      seenRoleIds.add(r.id);
      signerRoles.push(r);
    }
  }
  return {
    ...first,
    fields,
    // Listings blocks across bundle templates are kept as a union (rare to
    // overlap; positions/columns differ per template). The form uses them
    // only to decide whether to show the listings picker — actual rendering
    // happens per-template at send time.
    listingsBlocks: templates.flatMap((tp) => tp.listingsBlocks || []),
    signerRoles
  };
};

/**
 * Realtor profile keys that map to a `realtor.*` prefill source. Used by the
 * missing-fields detection to mark the matching realtor-card input red and
 * to derive an i18n label for the warning panel.
 */
const REALTOR_SOURCE_TO_USER_KEY: Partial<Record<SignaturePrefillSource, keyof User>> = {
  "realtor.name": "ownerFullName",
  "realtor.phone": "ownerPhoneNumber",
  "realtor.company": "companyName",
  "realtor.license": "realtorLicense",
  "realtor.company_id": "companyId",
  "realtor.company_logo": "companyLogoUrl",
  "realtor.signature_image": "signatureImageUrl"
};

/** user_info_block sub-line → User profile key. */
const USER_INFO_BLOCK_PROFILE_KEY: Record<UserInfoBlockLineKey, keyof User> = {
  name: "ownerFullName",
  license: "realtorLicense",
  phone: "ownerPhoneNumber",
  companyId: "companyId",
  company: "companyName"
};

/** user_info_block sub-line → friendly Hebrew label for the warning panel. */
const USER_INFO_BLOCK_LABEL: Record<UserInfoBlockLineKey, string> = {
  name: "שם המתווך",
  license: "מס' רישיון",
  phone: "טלפון המתווך",
  companyId: "ח.פ/עוסק מורשה",
  company: "חברת המתווך"
};

// Contacts in this app store names as `callerFirstName` + `callerLastName` — NOT `name` —
// and the PHONE IS THE DOCUMENT ID, not a field. Reading the wrong key was the cause of
// the "phone fills, name shows missing" bug.
//
// It read `callerPhoneNumber`, which looked right and was wrong: that field is absent on
// ~90% of contact documents (measured 2026-09-01: 267 of 2,683 across 60 accounts) and
// only appeared to work because `postProcessContact` overwrites it with the id on the read
// paths that call it. A signature sent from a surface that skips that normaliser had an
// empty signer phone. `c.id` is the identity and cannot be absent.
const buildContactContext = (c: RealEstateContact | null) => {
  if (!c) return undefined;
  return {
    contactName: getFullName(c.callerFirstName, c.callerLastName),
    phoneNumber: c.id || "",
    email: c.mail || "",
    idNumber: c.tehudatZehut || "",
    additionalPhoneNumbers: c.additionalPhoneNumbers || [],
    address: c.address || null
  };
};

/** Resolve the signer identity without branching inside the component's hook
 *  code path. The repository's older react-hooks lint plugin treats ternary
 *  expressions before later hooks as conditional hook execution. */
const resolveSignerIdentity = (options: {
  manual: boolean;
  contact: RealEstateContact | null;
  manualName: string;
  manualPhone: string;
}): { name: string; phone: string; email?: string; contactId?: string } => {
  if (options.manual) {
    return {
      name: options.manualName.trim(),
      phone: options.manualPhone.trim()
    };
  }
  return {
    name: getFullName(options.contact?.callerFirstName, options.contact?.callerLastName),
    phone: options.contact?.id || "",
    email: options.contact?.mail,
    contactId: options.contact?.id
  };
};

/**
 * Type-aware input for one document-value row. Both the per-source override
 * inputs and the sender-typed inputs route through this so they share a
 * single rendering + validation surface — without it the form rendered every
 * field as a plain text TextField regardless of `field.type`, so a checkbox
 * looked like a free-text box, a date had no picker, and email/phone/number/
 * id-number values weren't validated before send. The actual value-shape
 * decisions (what gets stored and how it's later rendered into the document)
 * live in `signaturesHtmlSend.ts`; this component only owns the form UX.
 */
type SignatureFormFieldInputProps = {
  field: SignatureTemplateField;
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder?: string;
  required?: boolean;
  /** External "missing in the warning panel" flag — paints the input red
   *  and shows `missingHelperText` when no validation error overrides it. */
  isMissing?: boolean;
  missingHelperText?: string;
};

// Preset values offered as quick-pick toggles for a percentage field. Stored
// as strings (matching the field value contract) so the toggle-group value
// comparison is a plain equality check.
const PERCENTAGE_PRESETS = ["1", "1.5", "2"] as const;
type PercentagePresetValue = typeof PERCENTAGE_PRESETS[number];
const isPercentagePreset = (v: string): v is PercentagePresetValue =>
  (PERCENTAGE_PRESETS as readonly string[]).includes(v);

function PercentagePickerInput({
  value,
  onChange,
  label,
  required,
  isMissing,
  missingHelperText
}: Omit<SignatureFormFieldInputProps, "field" | "placeholder">): JSX.Element {
  const { t } = useTranslation();

  // "Custom mode" is sticky local state — once the user picks Custom, the
  // input stays on screen even if they blank it out, so they don't get
  // bounced back to the preset row mid-typing. Initial value derives from
  // the incoming value: if it's a non-empty non-preset, the form was hydrated
  // from a previously-typed custom value (e.g. defaultValue on the field).
  const [customMode, setCustomMode] = useState<boolean>(
    !!value && !isPercentagePreset(value)
  );

  const presetSelected = isPercentagePreset(value) && !customMode ? value : null;

  const handleToggle = (_: unknown, next: string | null) => {
    if (next === "custom") {
      setCustomMode(true);
      if (isPercentagePreset(value)) onChange("");
      return;
    }
    if (next === null) {
      // User clicked the active preset → deselect.
      setCustomMode(false);
      onChange("");
      return;
    }
    setCustomMode(false);
    onChange(next);
  };

  // Allow only digits + one decimal point while typing. Clamping happens on
  // blur (per UX spec) so the user can freely type "12" → "120" → blur and
  // see it snap to 100, rather than fighting the clamp every keystroke.
  const handleCustomChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    const normalized =
      firstDot === -1
        ? cleaned
        : cleaned.slice(0, firstDot + 1) +
          cleaned.slice(firstDot + 1).replace(/\./g, "");
    onChange(normalized);
  };

  const handleCustomBlur = () => {
    if (!value || value === ".") {
      onChange("");
      return;
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
      onChange("");
      return;
    }
    const clamped = Math.min(100, Math.max(0, n));
    const rounded = Math.round(clamped * 100) / 100;
    onChange(String(rounded));
  };

  const showError = !!isMissing;

  return (
    <Stack spacing={0.5}>
      <Typography
        variant="caption"
        color={showError ? "error" : "textSecondary"}
      >
        {label}
        {required ? " *" : ""}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          value={customMode ? null : presetSelected}
          exclusive
          size="small"
          onChange={handleToggle}
          sx={{
            "& .MuiToggleButton-root.Mui-selected": {
              color: "primary.contrastText",
              backgroundColor: "primary.main",
              "&:hover": { backgroundColor: "primary.light" }
            }
          }}
        >
          <ToggleButton value="1">1%</ToggleButton>
          <ToggleButton value="1.5">1.5%</ToggleButton>
          <ToggleButton value="2">2%</ToggleButton>
          {!customMode && (
            <ToggleButton value="custom">
              {t("signatures.newDoc.percentageInput.custom")}
            </ToggleButton>
          )}
        </ToggleButtonGroup>
        {customMode && (
          <TextField
            autoFocus
            size="small"
            value={value}
            onChange={(e) => handleCustomChange(e.target.value)}
            onBlur={handleCustomBlur}
            inputProps={{
              inputMode: "decimal",
              dir: "ltr",
              style: { width: 56, textAlign: "right" }
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            error={showError}
          />
        )}
      </Stack>
      {showError && missingHelperText && (
        <Typography variant="caption" color="error">
          {missingHelperText}
        </Typography>
      )}
    </Stack>
  );
}

// Dynamic-price field: the stored value IS the final display string that gets
// baked into the document — a commission percentage ("1%" / "1.5%" / "2%" /
// custom "3.25%"), an absolute amount ("₪1,500,000", comma-grouped with the
// shekel sign), or the fixed one-month-rent label. Mirrors
// PercentagePickerInput's preset-row UX with an extra amount mode whose input
// formats live while typing.
//
// The template's `priceFieldConfig` adapts the picker for rental templates:
//   - `showPercentOptions` (default true) toggles the %-preset buttons.
//   - `percentSuffix` / `amountSuffix` are appended after the picked value and
//     baked in ("1.5%" → "1.5% ממחיר הנכס"), so the renderer stays untouched.
//   - `showOneMonthRentOption` (default false) surfaces a one-click
//     "דמי שכירות עבור חודש אחד" choice.
const PRICE_PERCENT_PRESETS = ["1%", "1.5%", "2%"];

// Append a suffix after a picked value ("1.5%" + "ממחיר הנכס" →
// "1.5% ממחיר הנכס"). Empty suffix leaves the value untouched.
const appendPriceSuffix = (core: string, suffix: string): string =>
  suffix ? `${core} ${suffix}` : core;

// Reverse of appendPriceSuffix — recover the picked value from a stored one so
// the toggle/inputs can hydrate. No-op when the suffix isn't present.
const stripPriceSuffix = (value: string, suffix: string): string =>
  suffix && value.endsWith(suffix)
    ? value.slice(0, value.length - suffix.length).trimEnd()
    : value;

/**
 * Default for a dynamic-fee ('price') field, derived from the field's own
 * picker config (the two option sets are mutually exclusive in practice):
 * a one-month-rent option means a rental template → default to one month's
 * rent; %-presets mean a sale template → default to 2%. Either way the seed
 * is a value the picker renders as a selected preset.
 */
const priceSeedForField = (field: SignatureTemplateField): string | null => {
  const cfg = field.priceFieldConfig ?? {};
  if (cfg.showOneMonthRentOption) return PRICE_ONE_MONTH_RENT_LABEL;
  if (cfg.showPercentOptions ?? true)
    return appendPriceSuffix("2%", cfg.percentSuffix ?? DEFAULT_PRICE_PERCENT_SUFFIX);
  return null;
};

function PricePickerInput({
  field,
  value,
  onChange,
  label,
  required,
  isMissing,
  missingHelperText
}: Omit<SignatureFormFieldInputProps, "placeholder">): JSX.Element {
  const { t } = useTranslation();

  const cfg = field.priceFieldConfig ?? {};
  const showPercentOptions = cfg.showPercentOptions ?? true;
  const showOneMonthRent = cfg.showOneMonthRentOption ?? false;
  // Undefined percentSuffix falls back to the default; an explicit "" disables it.
  const percentSuffix = cfg.percentSuffix ?? DEFAULT_PRICE_PERCENT_SUFFIX;
  const amountSuffix = cfg.amountSuffix ?? "";

  // Classify a stored (suffixed) value back into its picker kind + core value.
  // "other" is any free-text value the realtor typed that isn't a rent label,
  // a percentage, or a ₪-amount.
  const classify = (
    v: string
  ): { kind: "rent" | "percent" | "amount" | "other" | "empty"; core: string } => {
    if (!v) return { kind: "empty", core: "" };
    if (v === PRICE_ONE_MONTH_RENT_LABEL) return { kind: "rent", core: v };
    const asPercent = stripPriceSuffix(v, percentSuffix);
    if (asPercent.endsWith("%")) return { kind: "percent", core: asPercent };
    const asAmount = stripPriceSuffix(v, amountSuffix);
    if (asAmount.startsWith("₪")) return { kind: "amount", core: asAmount };
    return { kind: "other", core: v };
  };

  // Sticky mode — once the user picks custom-%, amount, or free text, the input
  // stays on screen even while blank. Initial mode derives from a hydrated value.
  const [mode, setMode] = useState<"customPercent" | "amount" | "otherText" | null>(() => {
    const c = classify(value);
    if (c.kind === "percent" && !PRICE_PERCENT_PRESETS.includes(c.core))
      return showPercentOptions ? "customPercent" : null;
    if (c.kind === "amount") return "amount";
    if (c.kind === "other") return "otherText";
    return null;
  });

  const cls = classify(value);
  let presetSelected: string | null = null;
  if (!mode) {
    if (cls.kind === "rent") presetSelected = "oneMonthRent";
    else if (cls.kind === "percent" && PRICE_PERCENT_PRESETS.includes(cls.core))
      presetSelected = cls.core;
  }
  const toggleValue = mode ?? presetSelected;

  const handleToggle = (_: unknown, next: string | null) => {
    if (next === "customPercent" || next === "amount" || next === "otherText") {
      setMode(next);
      onChange("");
      return;
    }
    setMode(null);
    if (next === "oneMonthRent") {
      onChange(PRICE_ONE_MONTH_RENT_LABEL);
      return;
    }
    // A %-preset ("1%"/"1.5%"/"2%") carries the percent suffix; null = deselect.
    onChange(next ? appendPriceSuffix(next, percentSuffix) : "");
  };

  // Free-text ("other") value is stored verbatim — no suffix, no formatting.
  const otherTextValue = mode === "otherText" ? value : "";

  // Custom percent: digits + one decimal point while typing; clamp to
  // [0, 100] on blur (same UX as PercentagePickerInput). The stored string
  // always carries the trailing "%" (+ percent suffix) so it's self-contained.
  const customPercentDigits =
    mode === "customPercent" ? stripPriceSuffix(value, percentSuffix).replace(/%$/, "") : "";
  const handleCustomPercentChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    const normalized =
      firstDot === -1
        ? cleaned
        : cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
    onChange(normalized ? appendPriceSuffix(`${normalized}%`, percentSuffix) : "");
  };
  const handleCustomPercentBlur = () => {
    const n = Number(customPercentDigits);
    if (!customPercentDigits || Number.isNaN(n)) {
      onChange("");
      return;
    }
    const clamped = Math.min(100, Math.max(0, n));
    onChange(appendPriceSuffix(`${Math.round(clamped * 100) / 100}%`, percentSuffix));
  };

  // Amount: digits only; the input shows the comma-grouped number live and
  // the stored value carries the shekel sign (+ amount suffix) so document +
  // input always agree.
  const amountDisplay =
    mode === "amount" ? stripPriceSuffix(value, amountSuffix).replace(/^₪/, "") : "";
  const handleAmountChange = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, "").slice(0, 12);
    onChange(
      digits
        ? appendPriceSuffix(`₪${Number(digits).toLocaleString("en-US")}`, amountSuffix)
        : ""
    );
  };

  const showError = !!isMissing;

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color={showError ? "error" : "textSecondary"}>
        {label}
        {required ? " *" : ""}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          value={toggleValue}
          exclusive
          size="small"
          onChange={handleToggle}
          sx={{
            "& .MuiToggleButton-root.Mui-selected": {
              color: "primary.contrastText",
              backgroundColor: "primary.main",
              "&:hover": { backgroundColor: "primary.light" }
            }
          }}
        >
          {showPercentOptions && <ToggleButton value="1%">1%</ToggleButton>}
          {showPercentOptions && <ToggleButton value="1.5%">1.5%</ToggleButton>}
          {showPercentOptions && <ToggleButton value="2%">2%</ToggleButton>}
          {showPercentOptions && (
            <ToggleButton value="customPercent">
              {t("signatures.newDoc.priceInput.customPercent")}
            </ToggleButton>
          )}
          <ToggleButton value="amount">
            {t("signatures.newDoc.priceInput.amount")}
          </ToggleButton>
          {showOneMonthRent && (
            <ToggleButton value="oneMonthRent">
              {t("signatures.newDoc.priceInput.oneMonthRent")}
            </ToggleButton>
          )}
          {showOneMonthRent && (
            <ToggleButton value="otherText">
              {t("signatures.newDoc.priceInput.other")}
            </ToggleButton>
          )}
        </ToggleButtonGroup>
        {mode === "customPercent" && (
          <TextField
            autoFocus
            size="small"
            value={customPercentDigits}
            onChange={(e) => handleCustomPercentChange(e.target.value)}
            onBlur={handleCustomPercentBlur}
            inputProps={{
              inputMode: "decimal",
              dir: "ltr",
              style: { width: 56, textAlign: "right" }
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            error={showError}
          />
        )}
        {mode === "amount" && (
          <TextField
            autoFocus
            size="small"
            value={amountDisplay}
            onChange={(e) => handleAmountChange(e.target.value)}
            inputProps={{
              inputMode: "numeric",
              dir: "ltr",
              style: { width: 110, textAlign: "right" }
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">₪</InputAdornment>
            }}
            error={showError}
          />
        )}
        {mode === "otherText" && (
          <TextField
            autoFocus
            size="small"
            fullWidth
            value={otherTextValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t("signatures.newDoc.priceInput.otherPlaceholder") as string}
            error={showError}
            sx={{ minWidth: 200 }}
          />
        )}
      </Stack>
      {showError && missingHelperText && (
        <Typography variant="caption" color="error">
          {missingHelperText}
        </Typography>
      )}
    </Stack>
  );
}

// Quick-pick horizons offered by the date dropdown ("in N months"), plus a
// custom option that reveals the native date picker. Sender-typed date
// fields are seeded to DEFAULT_DATE_SEED_MONTHS out (effect in the form).
const DATE_QUICK_PICK_MONTHS = [1, 2, 3, 4, 5, 6] as const;
const DEFAULT_DATE_SEED_MONTHS = 6;

// Local (not UTC) YYYY-MM-DD for "today + N months" — the exact string an
// <input type="date"> produces, so quick-picks and the custom picker store
// the same shape and the renderer's date formatting handles both identically.
const isoDateInMonths = (months: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

/**
 * Date field input for the send form: a quick-pick dropdown ("in 1 month" …
 * "in 6 months") plus a custom option that opens the native date picker.
 * A quick-pick stores the concrete resolved date (not a relative marker), and
 * the concrete date is echoed next to the dropdown in the field's own display
 * format so "in 6 months" never reads as a mystery value.
 */
function DateQuickPickInput({
  field,
  value,
  onChange,
  label,
  required,
  isMissing,
  missingHelperText
}: Omit<SignatureFormFieldInputProps, "placeholder">): JSX.Element {
  const { t } = useTranslation();

  const monthsMatch = DATE_QUICK_PICK_MONTHS.find((m) => isoDateInMonths(m) === value);
  // Sticky custom mode — once the user picks Custom, the date input stays on
  // screen (seeded with the previously-picked date) until a quick-pick is
  // chosen again. Initializes true when hydrating a value that matches no
  // quick-pick (e.g. a hand-picked date kept across a template switch).
  const [customMode, setCustomMode] = useState<boolean>(
    !!value && monthsMatch === undefined
  );

  const selectValue = customMode ? "custom" : monthsMatch !== undefined ? String(monthsMatch) : "";

  const quickPickLabel = (months: number): string =>
    months === 1
      ? (t("signatures.newDoc.dateInput.inOneMonth") as string)
      : months === 2
      ? (t("signatures.newDoc.dateInput.inTwoMonths") as string)
      : (t("signatures.newDoc.dateInput.inMonths", { months }) as string);

  const handleSelect = (next: string): void => {
    if (next === "custom") {
      // Keep the current value — the picker opens on the quick-picked date
      // and the user adjusts from there.
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    onChange(isoDateInMonths(Number(next)));
  };

  const showError = !!isMissing;
  const resolvedDateText = !customMode && value ? formatDate(value, field.dateFormat) : "";

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color={showError ? "error" : "textSecondary"}>
        {label}
        {required ? " *" : ""}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <TextField
          select
          size="small"
          value={selectValue}
          onChange={(e) => handleSelect(e.target.value)}
          error={showError}
          sx={{ minWidth: 160 }}
        >
          {DATE_QUICK_PICK_MONTHS.map((m) => (
            <MenuItem key={m} value={String(m)}>
              {quickPickLabel(m)}
            </MenuItem>
          ))}
          <MenuItem value="custom">
            {t("signatures.newDoc.dateInput.custom")}
          </MenuItem>
        </TextField>
        {customMode && (
          <TextField
            autoFocus
            size="small"
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={showError}
          />
        )}
        {!customMode && resolvedDateText && (
          <Typography variant="body2" color="textSecondary" dir="ltr">
            {resolvedDateText}
          </Typography>
        )}
      </Stack>
      {showError && missingHelperText && (
        <Typography variant="caption" color="error">
          {missingHelperText}
        </Typography>
      )}
    </Stack>
  );
}

function SignatureFormFieldInput({
  field,
  value,
  onChange,
  label,
  placeholder,
  required,
  isMissing,
  missingHelperText
}: SignatureFormFieldInputProps): JSX.Element {
  const { t } = useTranslation();
  const validationCode = validateFieldValue(field, value);
  const validationError = validationCode
    ? (t(`signatures.newDoc.validation.${validationCode}`) as string)
    : null;

  // Percentage: quick-pick toggle (1% / 1.5% / 2% / Custom). Picking Custom
  // replaces the Custom button with a numeric input on the same row; on blur
  // the value is clamped to [0, 100] and rounded to 2 decimals so the stored
  // string is always within range without nagging the user mid-typing.
  if (field.type === "percentage") {
    return (
      <PercentagePickerInput
        value={value}
        onChange={onChange}
        label={label}
        required={required}
        isMissing={isMissing}
        missingHelperText={missingHelperText}
      />
    );
  }

  // Dynamic price: commission percentage presets (1 / 1.5 / 2 / custom) OR an
  // absolute ₪ amount — the stored string is the final formatted display value.
  if (field.type === "price") {
    return (
      <PricePickerInput
        field={field}
        value={value}
        onChange={onChange}
        label={label}
        required={required}
        isMissing={isMissing}
        missingHelperText={missingHelperText}
      />
    );
  }

  // Date: quick-pick dropdown ("in 1 month" … "in 6 months") + a custom
  // option that opens the native date picker. Sender-typed date fields are
  // seeded to six months out by the form's default-seeding effect.
  if (field.type === "date") {
    return (
      <DateQuickPickInput
        field={field}
        value={value}
        onChange={onChange}
        label={label}
        required={required}
        isMissing={isMissing}
        missingHelperText={missingHelperText}
      />
    );
  }

  if (field.type === "radio") {
    const config = field.radioFieldConfig;
    const showError = !!validationError || !!isMissing;
    const helperText = validationError || (isMissing ? missingHelperText : undefined);
    return (
      <FormControl required={required} error={showError} fullWidth>
        <FormLabel component="legend">
          {config?.title || label}
        </FormLabel>
        {config?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.75 }}>
            {config.description}
          </Typography>
        )}
        <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
          {(config?.options || []).map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={
                <Box sx={{ py: 0.25 }}>
                  <Typography variant="body2">{option.label}</Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              }
            />
          ))}
        </RadioGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  // Checkbox: stored as "true" / "" so empty/missing reads as unchecked
  // both in this form and in the renderer. Required + unchecked doesn't
  // get the asterisk treatment — checkboxes are inherently bi-state and
  // "must be checked" is rare; the realtor can still leave it unticked.
  if (field.type === "checkbox") {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={value === "true"}
            onChange={(_, checked) => onChange(checked ? "true" : "")}
          />
        }
        label={label}
      />
    );
  }

  // Map our schema's field.type → the closest HTML5 input type. The browser
  // takes care of the keypad (number/tel) and basic shape enforcement;
  // `validateFieldValue` adds the locale-specific rules (Israeli phone,
  // T.Z. checksum) on top. Date fields never reach here (branch above).
  const inputType =
    field.type === "number"
      ? "number"
      : field.type === "email"
      ? "email"
      : field.type === "phone"
      ? "tel"
      : "text";

  const showError = !!validationError || !!isMissing;
  const helperText =
    validationError || (isMissing ? missingHelperText : undefined);

  return (
    <TextField
      id={`signature-field-${field.id}`}
      size="small"
      type={inputType}
      required={required}
      label={label}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      InputLabelProps={{ shrink: true }}
      error={showError}
      helperText={helperText}
      inputProps={
        field.type === "phone"
          ? { dir: "ltr", inputMode: "tel" }
          : field.type === "number"
          ? { inputMode: "decimal", dir: "ltr" }
          : field.type === "email"
          ? { dir: "ltr", autoCapitalize: "none", spellCheck: false }
          : undefined
      }
    />
  );
}

function SignerPrefillInputs({ groups, values, locked, onValueChange, onLockChange, compact = false }: {
  groups: { key: string; field: SignatureTemplateField }[];
  values: Record<string, string>;
  locked: Record<string, boolean>;
  onValueChange: (key: string, value: string) => void;
  onLockChange: (key: string, value: boolean) => void;
  compact?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: compact ? { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } : "1fr", gap: 1.5 }}>
      {groups.map(({ key, field }) => (
        <Box key={key} sx={{ minWidth: 0 }}>
          <SignatureFormFieldInput
            field={field}
            value={values[key] || ""}
            onChange={(next) => onValueChange(key, next)}
            label={signerPrefillLabel(field)}
          />
          {!!(values[key] ?? "").trim() && (
            <Box sx={{ pl: 0.5 }}>
              <FormControlLabel
                sx={{ mr: 0, alignItems: "flex-start" }}
                control={<Checkbox size="small" checked={!locked[key]} onChange={(_, checked) => onLockChange(key, !checked)} />}
                label={<Typography variant="caption" sx={{ pt: 1 }}>{t("signatures.newSignatureForm.signerPrefillAllowEdit")}</Typography>}
              />
              {locked[key] && (
                <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: -0.5 }}>
                  {t("signatures.newSignatureForm.signerPrefillLockedHint")}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function NewSignatureForm({
  initialContactId,
  initialTemplateId,
  initialBundleId,
  initialRole,
  initialExclusivity,
  initialQuestionnaireOnly,
  initialListingIds,
  onSent,
  onBusyChange,
  editDocument
}: NewSignatureFormProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const { userSettings, isAdminOfTheOrganization } = useFirestore();
  const { updateContact } = useContactWrites();
  const { propertyTypes } = usePropertyTypes();
  const { enqueueSnackbar } = useSnackbar();

  // Cache-first: the globally-mounted dialog preloads templates+bundles at app
  // start, so the picker usually renders instantly; a fresh fetch below still
  // revalidates in the background.
  const [templates, setTemplates] = useState<SignatureTemplate[]>(() => getCachedTemplates() ?? []);
  const [loadingTemplates, setLoadingTemplates] = useState(() => !getCachedTemplates());
  const [selectedTemplate, setSelectedTemplate] = useState<SignatureTemplate | null>(null);
  const [bundles, setBundles] = useState<SignatureTemplateBundle[]>(() => getCachedBundles() ?? []);

  // ---- Bundle mode state ----
  // When `initialBundleId` is set, the form loads the bundle + all included
  // templates here. `selectedTemplate` is set to a SYNTHETIC merged view
  // (union of fields/listingsBlocks/signerRoles across `bundleTemplates`) so
  // all existing memos work unchanged. Send-time uses the real per-template
  // entries to render N HTML/PDF pairs.
  const [selectedBundle, setSelectedBundle] = useState<SignatureTemplateBundle | null>(null);
  const [bundleTemplates, setBundleTemplates] = useState<SignatureTemplate[]>([]);
  const [bundleLoading, setBundleLoading] = useState<boolean>(!!initialBundleId);
  // Per-user numbering slice (next number + optional prefix) for the
  // selected template — fetched separately because numbering lives in its
  // own collection. Used only for the preview; the server allocates the real
  // number at send time.
  const [selectedTemplateUserCounter, setSelectedTemplateUserCounter] = useState<REST_schema.SignatureTemplateCounterDto>({
    nextDocumentNumber: 1
  });
  // The editable document-number input (single-template mode only). Seeded
  // with the fetched next number so the realtor SEES what will be used, and
  // can type a different one; sent as `documentNumberOverride` only when it
  // differs from the fetched next. Digits-only string ("" = fall back to the
  // auto number). NEVER pre-seeded with a made-up value — while the fetch is
  // in flight the field renders a skeleton, and on fetch failure it hides
  // entirely (showing a fake "1" reads as real data).
  const [documentNumberInput, setDocumentNumberInput] = useState<string>("");
  const [counterFetchState, setCounterFetchState] = useState<"loading" | "ready" | "error">("loading");
  // Bundle mode: per-member counter slices, keyed by templateId — ONE batch
  // request (the same endpoint the templates-list page uses). Drives the
  // per-template number shown in the bundle banner AND the number stamped
  // into each rendered document at send time. A member with no counter doc
  // is genuinely at 1 (the batch endpoint omits it) — that's real data, not
  // a fallback; on batch FAILURE the numbers are hidden instead.
  const [bundleCounters, setBundleCounters] = useState<Record<string, REST_schema.SignatureTemplateCounterDto>>({});
  const [bundleCountersState, setBundleCountersState] = useState<"loading" | "ready" | "error">("loading");
  /**
   * Per-member editable number inputs, keyed by templateId — the bundle
   * counterpart of `documentNumberInput`. Seeded with each member's fetched
   * next number; a value differing from the fetched next is sent as that
   * document's `documentNumberOverride`. Digits-only ("" = auto number).
   */
  const [bundleNumberInputs, setBundleNumberInputs] = useState<Record<string, string>>({});
  const [primarySigner, setPrimarySigner] = useState<RealEstateContact | null>(null);
  const [manualSignerName, setManualSignerName] = useState("");
  const [manualSignerPhone, setManualSignerPhone] = useState("");
  const manualSignerMode = selectedTemplate?.signerInputMode === "manual";
  const signerIdentity = resolveSignerIdentity({
    manual: manualSignerMode,
    contact: primarySigner,
    manualName: manualSignerName,
    manualPhone: manualSignerPhone
  });
  const { name: signerName, phone: signerPhone, email: signerEmail } = signerIdentity;

  /**
   * Is the chosen signer somebody a COLLEAGUE is designated to handle?
   *
   * Shares the module store with the picker directly above, so choosing a contact whose
   * option row already carried the badge issues no second request.
   */
  const { handler: signerHandler } = useContactHandler(signerIdentity.contactId ?? null);
  const collidingSignerHandler = signerHandler && !signerHandler.isMe && !signerHandler.stale ? signerHandler : null;
  const [selectedListings, setSelectedListings] = useState<ProcessedListingData[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  // Optional realtor-provided seeds for SIGNER-filled fields, keyed by the
  // field's tag name (`linkGroupId || name`). Contact-sourced fields (name /
  // email / T.Z. / address) auto-populate from the picked contact (effect
  // below); every typed signer field is freely editable here. Shipped via
  // signers[].fieldValues so the sign page pre-fills its inputs — unless the
  // realtor LOCKS the value (below), which bakes it into the rendered PDF
  // instead.
  const [signerPrefillValues, setSignerPrefillValues] = useState<Record<string, string>>({});
  // Per-group "don't let the signer edit this" lock (true = locked). Only
  // meaningful while the group has a non-empty seed; a locked value renders
  // baked into the document (no signer input) via ctx.lockedSignerValues.
  const [signerPrefillLocked, setSignerPrefillLocked] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState("");
  // ---- Additional terms (send-time free-form clauses) ----
  // Only offered when the selected template (or any bundle member) carries
  // an additionalTermsBlock. Title defaults to "שונות"; the terms HTML comes
  // from the mini rich-text editor. `termsEmpty` mirrors TipTap's own
  // emptiness so a blank editor (one empty paragraph) sends NO terms.
  const [termsTitle, setTermsTitle] = useState<string>(DEFAULT_ADDITIONAL_TERMS_TITLE);
  const [termsHtml, setTermsHtml] = useState("");
  const [termsEmpty, setTermsEmpty] = useState(true);
  const [sending, setSending] = useState(false);
  // What the realtor sees while a preparation runs: accepted at, step, time left.
  const [progress, setProgress] = useState<PreparationProgress | null>(null);
  // Compiles the exact documents a click would send while the form sits ready,
  // so the click usually finds them compiled (see signaturePreparation.ts).
  const [prepareAhead] = useState(() => createPrepareAhead());
  useEffect(() => () => prepareAhead.stop(), [prepareAhead]);
  const preparingRef = useRef(false);
  const pendingDraftRef = useRef<SignatureDocument | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inline create-contact / create-listing dialogs launched from the send form.
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [listingDialogOpen, setListingDialogOpen] = useState(false);

  // ---- Realtor (sender) override state ----
  // Per-document overrides for the realtor profile fields. Merged on top of
  // userSettings → pickedUserSnapshot at render time. Never written back to
  // Firestore — these are local to this single send. Empty string means
  // "intentional clear" only when the underlying value is falsy; for a
  // truthy underlying value an empty override reverts to it (handled by
  // dropping empty entries from the map).
  const [userOverrides, setUserOverrides] = useState<Partial<User>>({});
  // Admin-only: pick another org user → that user's saved profile becomes
  // the base for the realtor fields. Manual overrides above still win.
  const [pickedUserEmail, setPickedUserEmail] = useState<string | null>(null);
  // Snapshot of the picked user's profile, fetched on demand. When present,
  // REPLACES the realtor-relevant subset of userSettings (including missing
  // values) so we don't leak the admin's defaults into another user's send.
  const [pickedUserSnapshot, setPickedUserSnapshot] = useState<Partial<User> | null>(null);
  const [realtorSectionOpen, setRealtorSectionOpen] = useState(false);

  // Per-source overrides for prefilled fields used in the template. Keyed
  // by `prefillSource` so a source referenced by N fields (e.g. two
  // contact_name placeholders) shares ONE override input — the realtor
  // edits it once and every instance updates together. Threaded into the
  // renderer for both preview and send. Realtor.* sources aren't here —
  // those flow through `userOverrides` (realtor-details card). listing.*
  // sources aren't here either — those flow through `listingOverrides`
  // below, which is per-listing rather than per-source.
  const [fieldOverrides, setFieldOverrides] = useState<
    Partial<Record<SignaturePrefillSource, string>>
  >({});

  // Per-document per-listing edits: address parts (street / house number /
  // apartment / floor / city) plus price / rooms / sqm. Keyed by listing.id so
  // removing-and-readding the same listing preserves edits (and so two
  // listings can have different prices in the same document). Edits never
  // write back to the listing record — local to this single send. Two jobs:
  // filling in address parts the listing is missing (a contract has to
  // identify its property), and tweaking what gets written into this
  // particular contract (e.g. a one-off agreed price) without dirtying the
  // source listing.
  const [listingOverrides, setListingOverrides] = useState<
    Record<string, ListingOverride>
  >({});
  const setListingOverride = useCallback(
    (listingId: string, key: keyof ListingOverride, value: string) => {
      setListingOverrides((prev) => ({
        ...prev,
        [listingId]: { ...(prev[listingId] || {}), [key]: value }
      }));
    },
    []
  );

  /**
   * Fold a successful write-back into the local listing snapshot, and drop the
   * now-redundant address overrides.
   *
   * Without this the card would keep reading a stale record and go on offering
   * to save the same values, since "has unsaved edits" is a diff between the
   * form and the record. Firestore is already updated; this just stops our copy
   * from lying about it (Algolia's reindex lands later and is not awaited).
   */
  const applyListingRecordUpdate = useCallback(
    (listingId: string, parts: ListingAddressParts) => {
      const toNumber = (raw: string): number | undefined => {
        const trimmed = raw.trim();
        if (!trimmed) return undefined;
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : undefined;
      };
      setSelectedListings((prev) =>
        prev.map((l) =>
          l.id === listingId
            ? {
                ...l,
                address: {
                  ...l.address,
                  street: parts.street.trim(),
                  city: parts.city.trim(),
                  houseNumber: toNumber(parts.houseNumber),
                  apartmentNumber: toNumber(parts.apartmentNumber),
                  floor: toNumber(parts.floor)
                }
              }
            : l
        )
      );
      setListingOverrides((prev) => {
        const existing = prev[listingId];
        if (!existing) return prev;
        // Keep the non-address edits (price / rooms / sqm / redacted address) —
        // those are document-only and were never part of the write-back.
        const kept = { ...existing };
        for (const key of LISTING_ADDRESS_PART_KEYS) delete kept[key];
        return { ...prev, [listingId]: kept };
      });
    },
    []
  );

  // Hidden-addresses send toggle. Only meaningful (and only rendered) when
  // the picked template has a listings block with `allowHiddenAddresses`;
  // defaults to ON for such templates (effect below re-syncs on template
  // change). When on: the table shows redacted addresses + the consent
  // bullets, and an addresses addendum (full addresses + signature) is
  // generated and presented to the signer AFTER the main signature.
  const [hideAddresses, setHideAddresses] = useState(false);

  // Phone-OTP enforcement for the signers. Default ON — unchecking means the
  // signer opens the document straight from the link, and the audit
  // certificate records that SMS verification was waived by the sender.
  const [enforceOtp, setEnforceOtp] = useState(true);

  // ---- Load templates ----
  // Skip the auto-select-from-initialTemplateId when we're in bundle mode —
  // the bundle effect below owns `selectedTemplate` in that case (sets it to
  // the synthetic merged template). The initial selection is applied at most
  // ONCE (cached data may resolve it before the fresh fetch lands — the
  // revalidation must not stomp a selection the user changed in between).
  const initialTemplateAppliedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const apply = (tpls: SignatureTemplate[]): void => {
      if (cancelled) return;
      setTemplates(tpls);
      setLoadingTemplates(false);
      if (initialTemplateId && !initialBundleId && !initialTemplateAppliedRef.current) {
        const found = tpls.find((tpl) => tpl.id === initialTemplateId) ?? getCachedTemplateById(initialTemplateId);
        if (found) {
          initialTemplateAppliedRef.current = true;
          setSelectedTemplate(found);
        } else {
          // Direct links can reference an accessible system/org template the
          // user has never favorited. Resolve it for new sends as well as edits.
          initialTemplateAppliedRef.current = true;
          setLoadingTemplates(true);
          getTemplate(initialTemplateId)
            .then((tpl) => {
              if (!cancelled) setSelectedTemplate(tpl);
            })
            .catch((err) => {
              // eslint-disable-next-line no-console
              console.error("Failed to load linked template", err);
              if (!cancelled) setError(t("signatures.newSignatureForm.loadTemplatesError") as string);
            })
            .finally(() => {
              if (!cancelled) setLoadingTemplates(false);
            });
        }
      }
    };
    const cached = getCachedTemplates();
    if (cached) apply(cached);
    else setLoadingTemplates(true);
    listTemplates()
      .then(apply)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load templates", err);
        if (cancelled) return;
        setLoadingTemplates(false);
        // Only surface the error when we have nothing to show — a failed
        // background revalidation on top of cached data is invisible noise.
        if (!getCachedTemplates()) setError(t("signatures.newSignatureForm.loadTemplatesError") as string);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplateId, initialBundleId, t]);

  // ---- Load bundles for the picker (cache-first, silent errors) ----
  useEffect(() => {
    let cancelled = false;
    listBundles()
      .then((b) => {
        if (!cancelled) setBundles(b);
      })
      .catch(() => undefined); // bundles are optional in the picker — never block the form on them
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Enter bundle mode (from initialBundleId OR the template picker) ----
  // Loads every template in the bundle and sets `selectedTemplate` to a
  // SYNTHETIC merged-view template so every existing memo (sender-typed
  // groups, needsListing, sendContext, etc.) keeps working unchanged. Real
  // per-template renders happen at send time.
  const enterBundleMode = useCallback(
    async (bundle: SignatureTemplateBundle): Promise<void> => {
      // Cache-first: the app preloads /templates at start (the same cache the
      // single-template picker renders from), so a bundle whose members are
      // all cached enters bundle mode SYNCHRONOUSLY — the modal populates on
      // open exactly like a single-template send, no spinner, no empty flash.
      const cachedTemplates = getCachedTemplates();
      const fromCache = bundle.templateIds.map((id) => cachedTemplates?.find((tp) => tp.id === id));
      if (fromCache.length > 0 && fromCache.every(Boolean)) {
        const tpls = fromCache as SignatureTemplate[];
        setSelectedBundle(bundle);
        setBundleTemplates(tpls);
        setSelectedTemplate(buildMergedBundleTemplate(tpls));
        setBundleLoading(false);
        return;
      }
      // Fallback (bundle references templates outside the caller's list, or
      // the preload hasn't landed yet): show the bundle banner + spinner
      // IMMEDIATELY while the missing members are fetched — previously
      // nothing rendered until every per-template round trip finished.
      setSelectedBundle(bundle);
      setBundleLoading(true);
      try {
        const tpls = await Promise.all(
          bundle.templateIds.map((id) => cachedTemplates?.find((tp) => tp.id === id) ?? getTemplate(id))
        );
        if (tpls.length === 0) {
          setError(t("signatures.bundleEditor.loadError", "Failed to load bundle") as string);
          setSelectedBundle(null);
          return;
        }
        setBundleTemplates(tpls);
        setSelectedTemplate(buildMergedBundleTemplate(tpls));
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error("Failed to load bundle", err);
        setError(err?.response?.data?.error || err?.message || (t("signatures.bundleEditor.loadError", "Failed to load bundle") as string));
        // Revert to the picker — a bundle banner with no loaded templates is
        // a dead end.
        setSelectedBundle(null);
        setBundleTemplates([]);
      } finally {
        setBundleLoading(false);
      }
    },
    [t]
  );

  const exitBundleMode = useCallback((): void => {
    setSelectedBundle(null);
    setBundleTemplates([]);
  }, []);

  // ---- Role quick-pick (default templates per signing scenario) ----
  // The realtor picks WHO is signing (buyer / seller / renter / landlord) and
  // the form auto-selects the default template configured for that scenario
  // (signature settings — user override falling back to the Webot default).
  // "custom" reveals the classic template dropdown. Opening the form with an
  // explicit template/bundle starts in custom mode so the selection shows.
  const validInitialRole =
    initialRole && SIGNATURE_QUICK_PICK_ROLES.includes(initialRole) ? initialRole : null;
  const [signRole, setSignRole] = useState<SignatureQuickPickRole | "custom" | null>(
    initialBundleId || initialTemplateId ? "custom" : validInitialRole
  );
  // The exclusivity checkbox of the roles that have one (seller / landlord).
  // ON by default for a seller: signing one up normally means taking the
  // listing exclusively. OFF for a landlord: a rental is usually not exclusive,
  // and a ticked box would add a binding document nobody asked for.
  const [withExclusivity, setWithExclusivity] = useState<boolean>(
    () =>
      initialExclusivity ??
      (!!validInitialRole && SIGNATURE_EXCLUSIVITY_ON_BY_DEFAULT.includes(validInitialRole))
  );
  // "שאלון בלבד" under a seller / landlord: the questionnaire-only document (the statutory owner
  // questionnaire, nothing else) INSTEAD of the role's order and its exclusivity. Off unless the
  // realtor ticks it or the property card opened the form for it.
  const [questionnaireOnly, setQuestionnaireOnly] = useState<boolean>(
    () =>
      !!initialQuestionnaireOnly &&
      !!validInitialRole &&
      SIGNATURE_QUESTIONNAIRE_ONLY_ROLES.includes(validInitialRole)
  );
  // Effective defaults (user override over Webot-wide). Seeded synchronously
  // from the session cache so the role's template resolves without a spinner;
  // null only until the first load lands, {} on load failure (the quick-pick
  // then degrades to the "no default configured" hint).
  const [defaultTemplates, setDefaultTemplates] = useState<SignatureDefaultTemplates | null>(
    () => getCachedSignatureDefaultTemplates()?.effective ?? null
  );
  // Why the picked role's templates are not selected: none is configured (a
  // hint + the manual dropdown, `roleState.noDefault`), or a fetch failed (the
  // note above "הכנת חוזה" names it, with "נסו שוב"). Null otherwise.
  const [roleIssue, setRoleIssue] = useState<RoleIssue | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSignatureDefaultTemplates()
      .then((settings) => {
        if (!cancelled) setDefaultTemplates(settings.effective);
      })
      .catch(() => {
        if (!cancelled) setDefaultTemplates((prev) => prev ?? {});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Resolve a template id without hitting the network when we can avoid it:
   * the user's own list first, then the session's by-id cache (which the
   * send-data preload warms with exactly these default templates), and only
   * then a fetch. Returning a already-resolved value keeps role clicks instant.
   */
  const resolveTemplateFromCache = useCallback(
    (templateId: string): SignatureTemplate | null =>
      templates.find((tp) => tp.id === templateId) ?? getCachedTemplateById(templateId),
    [templates]
  );

  const resolveTemplateById = useCallback(
    async (templateId: string): Promise<SignatureTemplate> =>
      // Not in the user's own list nor cached (e.g. a Webot system template
      // they never favorited, on a cold cache) — fetch it directly.
      resolveTemplateFromCache(templateId) ?? (await getTemplate(templateId)),
    [resolveTemplateFromCache]
  );

  // Guards against out-of-order application when the role / exclusivity is
  // toggled faster than the template fetches resolve: only the newest call
  // may write state.
  const roleApplyTokenRef = useRef(0);

  /**
   * The documents the current selection will send, in send order. One entry
   * for a plain template, several in bundle mode (saved or ad-hoc). Drives
   * the read-only list in the quick-pick, so one and many render identically.
   */
  const roleDocuments = useMemo<SignatureTemplate[]>(
    () => (selectedBundle ? bundleTemplates : selectedTemplate ? [selectedTemplate] : []),
    [selectedBundle, bundleTemplates, selectedTemplate]
  );

  /** Commit a resolved document set: one template sends solo, several send as an ad-hoc bundle. */
  const applyResolvedRoleTemplates = useCallback(
    (resolved: SignatureTemplate[], role: SignatureQuickPickRole): void => {
      setRoleIssue(null);
      if (resolved.length === 1) {
        exitBundleMode();
        setSelectedTemplate(resolved[0]);
        return;
      }
      // Enter bundle mode against a CLIENT-ONLY bundle — nothing is saved to
      // the user's bundle list. The send path recognises the sentinel id and
      // ships the templates as an ad-hoc group instead of a bundleId.
      setSelectedBundle({
        id: ADHOC_BUNDLE_ID,
        // Never read: this bundle only feeds the form's merged view. The
        // server sets the real owner on the documents it creates.
        ownerUserId: "",
        // What the pair is called in the sent list, e.g. "החתמת משכיר בבלעדיות".
        name: t(
          `signatures.newSignatureForm.rolePicker.${SIGNATURE_EXCLUSIVITY_TEMPLATE_KEYS[role]}BundleName`
        ) as string,
        templateIds: resolved.map((tp) => tp.id).filter(Boolean) as string[]
      } as SignatureTemplateBundle);
      setBundleTemplates(resolved);
      setSelectedTemplate(buildMergedBundleTemplate(resolved));
    },
    [exitBundleMode, t]
  );

  const applyRoleTemplate = useCallback(
    async (role: SignatureQuickPickRole, exclusive: boolean, questionnaire: boolean): Promise<void> => {
      if (defaultTemplates === null) return; // defaults not loaded yet — the effect below re-runs on load
      const token = ++roleApplyTokenRef.current;

      // Seller / landlord + exclusivity ships TWO documents — the role's
      // regular template AND its exclusivity one — sent together as an ad-hoc
      // bundle (one message to the signer, both docs grouped in the sent
      // list). Every other combination is a single document. "שאלון בלבד"
      // swaps the role for the questionnaire-only slot: one document, no order.
      // A role and its exclusivity pointing at the same template send it once.
      const { ids: templateIds } = roleTemplatePlan(role, exclusive, questionnaire, defaultTemplates);

      if (templateIds.length === 0) {
        exitBundleMode();
        setRoleIssue({ kind: "noDefault" });
        setSelectedTemplate(null);
        return;
      }

      // Fast path: every document is already in memory (the send-data preload
      // warms exactly these), so commit synchronously — no await, no spinner,
      // no perceptible delay on the click.
      const cached = templateIds.map(resolveTemplateFromCache);
      if (cached.every((tp): tp is SignatureTemplate => !!tp)) {
        applyResolvedRoleTemplates(cached as SignatureTemplate[], role);
        return;
      }

      // A template has to be fetched, and until it is here the selection is
      // still the previous one (the order alone under a ticked box, or the last
      // role's documents): "הכנת חוזה" waits and the note above it says why
      // (`roleDocumentsState`). A failed fetch keeps what did load, so the list
      // still shows it and unticking sends the order alone, by choice, and the
      // note names what did not load, with "נסו שוב".
      setRoleIssue(null);
      const settled = await Promise.allSettled(templateIds.map(resolveTemplateById));
      if (roleApplyTokenRef.current !== token) return; // superseded by a newer pick
      const resolved = settled.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []));
      if (resolved.length === templateIds.length) {
        applyResolvedRoleTemplates(resolved, role);
        return;
      }
      if (resolved.length > 0) {
        applyResolvedRoleTemplates(resolved, role);
      } else {
        exitBundleMode();
        setSelectedTemplate(null);
      }
      setRoleIssue({
        kind: "loadFailed",
        planIds: templateIds,
        failedIds: templateIds.filter((_, index) => settled[index].status === "rejected")
      });
    },
    [
      defaultTemplates,
      resolveTemplateFromCache,
      resolveTemplateById,
      applyResolvedRoleTemplates,
      exitBundleMode
    ]
  );

  // (Re-)apply the picked role's default template(s) whenever the role, the
  // exclusivity toggle, "שאלון בלבד", or the loaded defaults change.
  useEffect(() => {
    if (!signRole || signRole === "custom") return;
    void applyRoleTemplate(signRole, withExclusivity, questionnaireOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signRole, withExclusivity, questionnaireOnly, defaultTemplates]);

  // What the role row sends against what is selected. While they differ (a
  // template still on its way, or one that could not be loaded) "הכנת חוזה"
  // waits (`validate`) and the note above it says why: it must never prepare one
  // contract while the form says two.
  const roleState = roleDocumentsState({
    role: signRole,
    exclusive: withExclusivity,
    questionnaire: questionnaireOnly,
    defaults: defaultTemplates,
    selected: roleDocuments,
    issue: roleIssue
  });
  // The note's "נסו שוב": the same resolution, fetched afresh.
  const retryRoleDocuments = (): void => {
    if (signRole && signRole !== "custom") void applyRoleTemplate(signRole, withExclusivity, questionnaireOnly);
  };

  // The checkbox is offered only when ticking it would add a document: the
  // picked role has an exclusivity slot AND a template is set for it (the
  // user's own, else the Webot-wide one). A box that sends nothing extra is
  // worse than no box.
  const exclusivityKey =
    signRole && signRole !== "custom" ? SIGNATURE_EXCLUSIVITY_TEMPLATE_KEYS[signRole] : undefined;
  const exclusivityAvailable = !!exclusivityKey && !!defaultTemplates?.[exclusivityKey];
  // "שאלון בלבד" is offered to a seller / landlord when the questionnaire-only slot has a template,
  // and stays visible while ticked so a realtor who arrived with it on can untick it.
  const questionnaireOnlyRole =
    !!signRole && signRole !== "custom" && SIGNATURE_QUESTIONNAIRE_ONLY_ROLES.includes(signRole);
  const questionnaireOnlyAvailable =
    questionnaireOnlyRole && (questionnaireOnly || !!defaultTemplates?.[SIGNATURE_QUESTIONNAIRE_ONLY_TEMPLATE_KEY]);

  const handleRoleChange = (next: SignatureQuickPickRole | "custom" | null): void => {
    if (!next) return; // exclusive group — ignore deselect clicks
    if (next === "custom") {
      setRoleIssue(null);
      // The ad-hoc role+exclusivity pair isn't a saved bundle, so the picker
      // has no option to show for it. Drop back to its first real template
      // (the role's plain one) — otherwise the picker renders an option that
      // doesn't exist and a send would still ship both documents.
      if (selectedBundle?.id === ADHOC_BUNDLE_ID) {
        const firstTemplate = bundleTemplates[0] ?? null;
        exitBundleMode();
        setSelectedTemplate(firstTemplate);
      }
    }
    setSignRole(next);
    // Re-arm the role's own exclusivity default each time a role is (re-)picked;
    // clicking the already-selected role is a no-op here, so an explicit change
    // sticks for as long as the user stays on that role.
    setWithExclusivity(next !== "custom" && SIGNATURE_EXCLUSIVITY_ON_BY_DEFAULT.includes(next));
    // "שאלון בלבד" survives a move between seller and landlord (the same owner's questionnaire,
    // a sale or a rental) and is dropped by any other role.
    setQuestionnaireOnly((prev) => prev && next !== "custom" && SIGNATURE_QUESTIONNAIRE_ONLY_ROLES.includes(next));
  };

  // ---- Template picker options: bundles + single templates in ONE picker ----
  // A bundle option enters bundle mode (loads its templates, builds the
  // synthetic merged template); a template option exits it.
  type TemplatePickerOption =
    | { kind: "template"; optionId: string; template: SignatureTemplate }
    | { kind: "bundle"; optionId: string; bundle: SignatureTemplateBundle };
  const pickerOptions = useMemo<TemplatePickerOption[]>(
    () => [
      ...bundles.map((b) => ({ kind: "bundle" as const, optionId: `bundle:${b.id}`, bundle: b })),
      ...templates.map((tp) => ({ kind: "template" as const, optionId: `template:${tp.id}`, template: tp })),
      ...(selectedTemplate && !selectedBundle && !templates.some((tp) => tp.id === selectedTemplate.id)
        ? [{ kind: "template" as const, optionId: `template:${selectedTemplate.id}`, template: selectedTemplate }]
        : [])
    ],
    [bundles, templates, selectedTemplate, selectedBundle]
  );
  const pickerValue = useMemo<TemplatePickerOption | null>(() => {
    if (selectedBundle) return { kind: "bundle", optionId: `bundle:${selectedBundle.id}`, bundle: selectedBundle };
    if (selectedTemplate) return { kind: "template", optionId: `template:${selectedTemplate.id}`, template: selectedTemplate };
    return null;
  }, [selectedBundle, selectedTemplate]);
  const handlePickerChange = useCallback(
    (option: TemplatePickerOption | null): void => {
      if (!option) {
        exitBundleMode();
        setSelectedTemplate(null);
        return;
      }
      if (option.kind === "template") {
        exitBundleMode();
        setSelectedTemplate(option.template);
        return;
      }
      void enterBundleMode(option.bundle);
    },
    [enterBundleMode, exitBundleMode]
  );

  // ---- Open on a saved bundle (a `newSignatureBundle` link) ----
  // This effect only ever LOADS a bundle. Without one there is nothing to undo:
  // the bundle state starts empty, and the global dialog remounts the form
  // (`key`) whenever its link changes. It must not clear that state either: on
  // mount the role effect above may already have committed a seller / landlord
  // PAIR from the session caches in this same pass (every in-app open, once the
  // app's preload has run). Clearing it here left the merged view selected (the
  // order alone) under a ticked "החתמה בבלעדיות (יישלחו שני מסמכים)", and that is
  // what "הכנת חוזה" prepared, unless the defaults refresh came back and
  // re-ran the role effect.
  useEffect(() => {
    if (!initialBundleId) return;
    // Cache-first: bundle metadata is preloaded at app start alongside the
    // templates, so the usual open skips the /bundles/:id round trip entirely
    // (and, with a warm templates cache, enterBundleMode resolves
    // synchronously — the modal is populated on first paint).
    const cachedBundle = getCachedBundles()?.find((b) => b.id === initialBundleId);
    if (cachedBundle) {
      void enterBundleMode(cachedBundle);
      return;
    }
    let cancelled = false;
    setBundleLoading(true);
    getBundle(initialBundleId)
      .then((b) => {
        if (!cancelled) return enterBundleMode(b);
        return undefined;
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load bundle", err);
        if (!cancelled) {
          setBundleLoading(false);
          setError(err?.response?.data?.error || err?.message || (t("signatures.bundleEditor.loadError", "Failed to load bundle") as string));
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBundleId]);

  // ---- Load the per-user counter for the selected template ----
  // Counter lives in its own collection; fetch it whenever the selection
  // changes so the preview number reflects the user's own sequence (not the
  // template's shared state, which doesn't exist).
  useEffect(() => {
    // Every selection change restarts the lifecycle: skeleton until the
    // fetch lands, and no stale number from the previous template.
    setCounterFetchState("loading");
    setDocumentNumberInput("");
    if (!selectedTemplate?.id) {
      setSelectedTemplateUserCounter({ nextDocumentNumber: 1 });
      return;
    }
    let cancelled = false;
    getUserTemplateCounter(selectedTemplate.id)
      .then((slice) => {
        if (cancelled) return;
        setSelectedTemplateUserCounter(slice);
        setDocumentNumberInput(String(slice.nextDocumentNumber));
        setCounterFetchState("ready");
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load per-user counter for preview:", err);
        if (!cancelled) {
          setSelectedTemplateUserCounter({ nextDocumentNumber: 1 });
          setCounterFetchState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTemplate?.id]);

  // ---- Bundle mode: load every member's counter in one batch ----
  useEffect(() => {
    if (!selectedBundle || bundleTemplates.length === 0) {
      setBundleCounters({});
      setBundleNumberInputs({});
      setBundleCountersState("loading");
      return;
    }
    let cancelled = false;
    setBundleCountersState("loading");
    setBundleNumberInputs({});
    listUserTemplateCounters()
      .then((map) => {
        if (cancelled) return;
        setBundleCounters(map);
        // Seed every member's editable input with its fetched next number —
        // a member with no counter doc genuinely starts at 1.
        setBundleNumberInputs(
          Object.fromEntries(bundleTemplates.map((tp) => [tp.id, String(map[tp.id]?.nextDocumentNumber ?? 1)]))
        );
        setBundleCountersState("ready");
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load per-user counters for bundle:", err);
        if (!cancelled) setBundleCountersState("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBundle?.id, bundleTemplates]);

  /** Parsed per-member override — `null` when the input is empty (= auto number). */
  const parseBundleMemberOverride = (templateId: string): number | null => {
    const raw = (bundleNumberInputs[templateId] ?? "").trim();
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isInteger(n) && n >= 1 ? n : null;
  };
  const isBundleMemberNumberInvalid = (templateId: string): boolean => {
    const raw = (bundleNumberInputs[templateId] ?? "").trim();
    return raw !== "" && parseBundleMemberOverride(templateId) === null;
  };
  const anyBundleNumberInvalid = bundleTemplates.some((tp) => isBundleMemberNumberInvalid(tp.id));
  /** Formatted number stamped into one member's rendered document — the typed
   *  override when valid, else the member's fetched next (with its prefix). */
  const effectiveBundleMemberFormattedNumber = (templateId: string): string => {
    const slice = bundleCounters[templateId] ?? { nextDocumentNumber: 1 };
    const n = parseBundleMemberOverride(templateId) ?? slice.nextDocumentNumber;
    return `${slice.numberPrefix ?? ""}${n}`;
  };

  // Parsed override state. `null` = no valid explicit number (empty input →
  // auto). Invalid means the realtor typed something that parses to < 1 —
  // the field shows an error and send is blocked until fixed or cleared.
  const overrideDocumentNumber = useMemo(() => {
    const trimmed = documentNumberInput.trim();
    if (!trimmed) return null;
    const n = parseInt(trimmed, 10);
    return Number.isInteger(n) && n >= 1 ? n : null;
  }, [documentNumberInput]);
  const documentNumberInvalid = documentNumberInput.trim() !== "" && overrideDocumentNumber === null;
  /**
   * Number shown in the live preview — the typed override when valid, else
   * the fetched next. `null` while the counter is still loading (or failed):
   * the preview then stamps an empty number box rather than a made-up "1".
   */
  const effectiveNextDocumentNumber =
    overrideDocumentNumber ?? (counterFetchState === "ready" ? selectedTemplateUserCounter.nextDocumentNumber : null);

  // ---- Preselect contact (deep-link from the contacts table) ----
  // The preselected contact's LIVE DOCUMENT, read by id. The signer's document is what
  // the prefills below are taken from (email, ID number, address, extra phones), so it has
  // to be the whole contact. This used to be found in the caller's entire downloaded book,
  // which the form armed for the sake of this one lookup.
  const { contact: preselectedContact } = useContactByPhone(manualSignerMode ? null : initialContactId);
  useEffect(() => {
    if (manualSignerMode) return;
    if (!initialContactId) return;
    if (primarySigner?.id === initialContactId) return;
    if (preselectedContact) setPrimarySigner(preselectedContact);
  }, [initialContactId, preselectedContact, primarySigner, manualSignerMode]);

  // ---- Picked-user snapshot fetch ----
  // When an admin picks another org user, load that user's saved profile
  // so it becomes the realtor base for this send. We capture only the
  // realtor-stamp-relevant subset (name, phone, company, license, etc.) —
  // missing keys stay undefined so a missing license on the picked user
  // shows as missing in the document instead of silently falling back to
  // the admin's value. Manual overrides reset whenever the picker changes
  // so an edit made for user X doesn't leak into user Y.
  useEffect(() => {
    setUserOverrides({});
    if (!pickedUserEmail || pickedUserEmail === (userSettings as any)?.id) {
      setPickedUserSnapshot(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await db
          .collection("dashboard-users-v2")
          .doc(pickedUserEmail)
          .get();
        if (cancelled) return;
        const data = (snap.data() as Partial<User> | undefined) ?? {};
        setPickedUserSnapshot({
          ownerFullName: data.ownerFullName,
          ownerPhoneNumber: data.ownerPhoneNumber,
          companyName: data.companyName,
          realtorLicense: data.realtorLicense,
          companyId: data.companyId,
          companyLogoUrl: data.companyLogoUrl,
          signatureImageUrl: data.signatureImageUrl
        });
        // Auto-expand so the admin sees the seeded values + any blanks.
        setRealtorSectionOpen(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load picked user's settings:", err);
        if (!cancelled) setPickedUserSnapshot({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pickedUserEmail, userSettings]);

  /**
   * Effective realtor profile used by the renderer. Merge order:
   *   1. userSettings (logged-in user) — base
   *   2. pickedUserSnapshot — overwrites every key in PICKED_USER_FIELDS,
   *      EVEN with undefined, so a missing field on the picked user
   *      doesn't silently fall back to the admin's value.
   *   3. userOverrides — manual edits in the realtor card win over both.
   *      Empty / whitespace overrides are dropped (= "no override").
   */
  const PICKED_USER_FIELDS: (keyof User)[] = [
    "ownerFullName",
    "ownerPhoneNumber",
    "companyName",
    "realtorLicense",
    "companyId",
    "companyLogoUrl",
    "signatureImageUrl"
  ];
  const effectiveUser = useMemo(() => {
    const merged: any = { ...userSettings };
    if (pickedUserSnapshot) {
      for (const k of PICKED_USER_FIELDS) {
        merged[k] = (pickedUserSnapshot as any)[k];
      }
    }
    for (const [k, v] of Object.entries(userOverrides)) {
      if (typeof v === "string" && v.trim() === "") continue;
      if (v != null) merged[k] = v;
    }
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings, pickedUserSnapshot, userOverrides]);

  // Inputs in the realtor card show only what the realtor has actively
  // typed. The saved profile value renders as a placeholder so an empty
  // input doesn't look like "I want to clear this".
  const setOverride = (key: keyof User, value: string) => {
    setUserOverrides((prev) => {
      const next = { ...prev };
      if (value === "") {
        delete (next as any)[key];
      } else {
        (next as any)[key] = value;
      }
      return next;
    });
  };
  const overrideValue = (key: keyof User): string => {
    const v = userOverrides[key];
    return v == null ? "" : ((v as string) ?? "");
  };
  const overridePlaceholder = (key: keyof User): string => {
    const source = pickedUserSnapshot ?? (userSettings as any);
    return ((source as any)?.[key] as string) ?? "";
  };

  // ---- Sender-typed fields (the form needs an input for each group) ----
  // Fields that share a linkGroupId render as ONE input — typing the value
  // once stamps it onto every linked occurrence at send time. The
  // representative field (first in document order) supplies the input
  // label; the lookup `key` is what the renderer reads back via
  // `fieldValues[linkGroupId || name]`.
  //
  // `required` is true if ANY field in the group has required=true — the
  // realtor's intent for "this must be filled" propagates across the link
  // group even if only one sibling was originally marked required.
  const senderTypedGroups = useMemo<{
    key: string;
    field: SignatureTemplateField;
    required: boolean;
  }[]>(() => {
    if (!selectedTemplate) return [];
    const idxByKey = new Map<string, number>();
    const out: { key: string; field: SignatureTemplateField; required: boolean }[] = [];
    for (const f of selectedTemplate.fields) {
      if (!isSenderTyped(f)) continue;
      const key = f.linkGroupId || f.name;
      const existingIdx = idxByKey.get(key);
      if (existingIdx === undefined) {
        idxByKey.set(key, out.length);
        out.push({ key, field: f, required: !!f.required });
      } else if (f.required) {
        out[existingIdx].required = true;
      }
    }
    return out;
  }, [selectedTemplate]);

  // ---- Seed default values for sender-typed fields ----
  // Fee (dynamic-price) fields default from their own picker config — one
  // month's rent when the rental option is offered, else 2% — and date
  // fields default to six months out. The ref remembers what WE seeded so a
  // later template switch re-seeds only values the realtor hasn't touched;
  // anything the realtor typed (or cleared) is never overwritten.
  const autoSeededFieldValuesRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (senderTypedGroups.length === 0) return;
    setFieldValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const { key, field: f } of senderTypedGroups) {
        // A template author's explicit defaultValue wins over the generic
        // fee/date seeding — it's already the renderer's empty-input
        // fallback, so don't stomp it with a synthetic value.
        if (f.defaultValue) continue;
        let seed: string | null = null;
        if (f.type === "price") seed = priceSeedForField(f);
        else if (f.type === "date") seed = isoDateInMonths(DEFAULT_DATE_SEED_MONTHS);
        if (!seed || prev[key] === seed) continue;
        const current = prev[key];
        const untouched =
          current === undefined || current === autoSeededFieldValuesRef.current[key];
        if (!untouched) continue;
        next[key] = seed;
        autoSeededFieldValuesRef.current[key] = seed;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [senderTypedGroups]); 
 
  // ---- Signer-prefill groups (typed fields the SIGNER fills) ----
  // Every typed signer-filled field the realtor may optionally pre-fill
  // before sending. Contact-sourced groups (name / email / T.Z. / address)
  // auto-seed from the picked contact; the rest start empty. Same grouping
  // rules as senderTypedGroups: one entry per `linkGroupId || name` (first
  // occurrence supplies the label field), `required` ORs across the group.
  // Works in bundle mode too — the merged template unions every member's
  // fields. Excluded: signature/initials/image (nothing to type), checkbox
  // (binary — a pre-ticked box belongs to prefilled/fixed fill modes), price
  // (realtor-picked by design), and the system-stamped signing date.
  const signerPrefillGroups = useMemo<{
    key: string;
    field: SignatureTemplateField;
    source: SignerPrefillContactSource | null;
    required: boolean;
  }[]>(() => {
    if (!selectedTemplate) return [];
    const idxByKey = new Map<string, number>();
    const out: {
      key: string;
      field: SignatureTemplateField;
      source: SignerPrefillContactSource | null;
      required: boolean;
    }[] = [];
    for (const f of selectedTemplate.fields) {
      if (effectiveFillMode(f) !== "signer") continue;
      if (
        f.type === "signature" ||
        f.type === "initials" ||
        f.type === "image" ||
        f.type === "checkbox" ||
        f.type === "price"
      )
        continue;
      if ((f.prefillSource ?? f.labelKey) === "signing_date") continue;
      const source = resolveSignerPrefillSource(f);
      const key = f.linkGroupId || f.name;
      const existingIdx = idxByKey.get(key);
      if (existingIdx === undefined) {
        idxByKey.set(key, out.length);
        out.push({ key, field: f, source, required: !!f.required });
      } else if (f.required) {
        out[existingIdx].required = true;
      }
    }
    return out;
  }, [selectedTemplate]);

  // The manual signer card is already the single source of truth for the
  // signer's name. Keep that field in the document as signer-editable, but
  // don't render a duplicate name input among the remaining signer details.
  const visibleSignerPrefillGroups = useMemo(
    () =>
      manualSignerMode
        ? signerPrefillGroups.filter((g) => g.source !== "contact.name")
        : signerPrefillGroups,
    [manualSignerMode, signerPrefillGroups]
  );

  // Switching templates resets the signer-prefill surface entirely — the
  // groups belong to the old template's fields. Runs BEFORE the contact-seed
  // effect below (definition order) so sourced groups re-seed into a clean
  // map in the same commit. In edit mode the edit-prefill effect (defined
  // later, same-commit writes win) restores the document's values on top.
  useEffect(() => {
    setSignerPrefillValues({});
    setSignerPrefillLocked({});
  }, [selectedTemplate?.id]);

  // Reseed the CONTACT-SOURCED signer-prefill groups from the picked contact
  // whenever the contact or the selected template changes. Realtor edits to
  // those groups persist until then — the reseed intentionally overwrites
  // them (the new contact's data is the better default). A contact missing
  // the value (or no contact) seeds "", i.e. nothing is sent for that field.
  // Sourceless groups have no contact data and are left untouched (they only
  // reset on template change, above). EDIT MODE skips this entirely: the
  // document (snapshot / stored fieldValues) is the source of truth there,
  // and a late-resolving contact must not clobber the restored values.
  useEffect(() => {
    if (editDocument) return;
    setSignerPrefillValues((prev) => {
      const next = { ...prev };
      for (const g of signerPrefillGroups) {
        if (!g.source) continue;
        if (!manualSignerMode) {
          next[g.key] = contactValueForSignerPrefill(primarySigner, g.source);
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primarySigner?.id, selectedTemplate?.id, manualSignerMode]);

  /** Trimmed non-empty signer-prefill seeds, keyed by the signer TAG name
   *  (`linkGroupId || name`) — exactly the key the sign page's inputs read
   *  back via `prefilledValues`. Empty inputs are simply not seeded. */
  const nonEmptySignerPrefills = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const g of signerPrefillGroups) {
      // The name input also drives document seeds in edit-after-send mode.
      const v = (manualSignerMode && g.source === "contact.name"
        ? manualSignerName
        : signerPrefillValues[g.key] ?? "").trim();
      if (v) out[g.key] = v;
    }
    return out;
  }, [signerPrefillGroups, signerPrefillValues, manualSignerMode, manualSignerName]);

  /** The subset of non-empty seeds the realtor LOCKED ("signer can't edit").
   *  Threaded into the render context, where the signer branch bakes these
   *  values into the document instead of emitting a field tag — so the lock
   *  is enforced by construction (the field never reaches `sentFields`). */
  const lockedSignerValues = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [key, v] of Object.entries(nonEmptySignerPrefills)) {
      if (signerPrefillLocked[key]) out[key] = v;
    }
    return out;
  }, [nonEmptySignerPrefills, signerPrefillLocked]);

  // True when any listings block on the template SUPPORTS the hidden-addresses
  // mode. That surfaces the per-send checkbox below the listings picker; the
  // actual decision is `hideAddresses` (defaulting to ON for such templates —
  // the realtor picked a template built for the flow).
  const templateAllowsHiddenAddresses = useMemo(
    () => !!selectedTemplate?.listingsBlocks?.some((b) => b.allowHiddenAddresses),
    [selectedTemplate]
  );
  useEffect(() => {
    setHideAddresses(templateAllowsHiddenAddresses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateAllowsHiddenAddresses, selectedTemplate?.id]);

  // ---- EDIT MODE: prefill from the sent document ----
  // Runs ONCE, after the template selection (driven by initialTemplateId)
  // resolves to the document's template. Defined AFTER the template-driven
  // reset effects above so its writes (e.g. the document's hideAddresses
  // choice over the template default) win the same commit. Listings load
  // async by id; everything else is synchronous state seeding.
  const editPrefillAppliedRef = useRef(false);
  useEffect(() => {
    if (!editDocument || editPrefillAppliedRef.current) return;
    if (selectedTemplate?.id !== editDocument.templateId) return;
    editPrefillAppliedRef.current = true;
    const editSigner = editDocument.signers[0];
    if (selectedTemplate.signerInputMode === "manual" && editSigner) {
      setManualSignerName(editSigner.name || "");
      setManualSignerPhone(editSigner.phone || "");
    }
    if (editSigner?.fieldValues) setFieldValues({ ...editSigner.fieldValues });
    setNotes(editDocument.notes || "");
    setEnforceOtp(editDocument.otpRequired !== false);
    setHideAddresses(!!editDocument.hideAddresses);
    const snapshot = editDocument.sendFormSnapshot;
    if (snapshot?.additionalTerms?.html) {
      setTermsTitle(snapshot.additionalTerms.title || DEFAULT_ADDITIONAL_TERMS_TITLE);
      setTermsHtml(snapshot.additionalTerms.html);
      setTermsEmpty(false);
    }
    if (snapshot?.fieldOverrides) {
      setFieldOverrides(snapshot.fieldOverrides as Partial<Record<SignaturePrefillSource, string>>);
    }
    if (snapshot?.listingOverrides) {
      setListingOverrides(snapshot.listingOverrides as Record<string, ListingOverride>);
    }
    // Signer prefills: the snapshot restores both the value and the
    // "allow signer to edit" checkbox. Documents sent before the snapshot
    // carried signerPrefills fall back to the stored signer.fieldValues
    // (which include the seeds, merged at send time) — those restore as
    // editable, since pre-feature sends had no lock.
    if (snapshot?.signerPrefills) {
      const values: Record<string, string> = {};
      const locked: Record<string, boolean> = {};
      for (const [key, entry] of Object.entries(snapshot.signerPrefills)) {
        values[key] = entry.value;
        if (!entry.allowEdit) locked[key] = true;
      }
      setSignerPrefillValues(values);
      setSignerPrefillLocked(locked);
    } else if (editSigner?.fieldValues) {
      const values: Record<string, string> = {};
      for (const g of signerPrefillGroups) {
        const v = (editSigner.fieldValues[g.key] ?? "").trim();
        if (v) values[g.key] = v;
      }
      if (Object.keys(values).length > 0) setSignerPrefillValues(values);
    }
    if (editDocument.listingIds.length > 0) {
      void Promise.all(
        editDocument.listingIds.map((id) => fetchListingById(id).catch(() => null))
      ).then((listings) => {
        const resolved = listings.filter(Boolean) as ProcessedListingData[];
        if (resolved.length > 0) setSelectedListings(resolved);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDocument, selectedTemplate?.id]);

  const effectiveHideAddresses = templateAllowsHiddenAddresses && hideAddresses;

  // Listing contexts with per-listing overrides baked in. Single source of
  // truth for both the renderer (multi-row listings table) and downstream
  // single-listing reads (`resolveSourceValue` placeholders + missing
  // detection for listing.* sources) — so an override entered in the
  // listing-overrides card flows through every code path without per-call
  // re-merge logic.
  const effectiveListings = useMemo(
    () =>
      selectedListings.map((l) =>
        buildEffectiveListingContext(
          l,
          l.id ? listingOverrides[l.id] : undefined,
          effectiveHideAddresses
        )
      ),
    [selectedListings, listingOverrides, effectiveHideAddresses]
  );

  // ---- Build the SendRenderContext (used by both preview + actual send) ----
  const sendContext = useMemo<SendRenderContext | null>(() => {
    if (!selectedTemplate || !userSettings) return null;
    const role = selectedTemplate.signerRoles[0];
    // Additional terms ride the context only when the realtor actually typed
    // some — presence is what activates the template's terms block in the
    // renderer (preview AND send; bundle sends spread this context per
    // member, so every member with a block renders the same terms).
    const additionalTerms =
      !termsEmpty && termsHtml.trim() ? { title: termsTitle, html: termsHtml } : undefined;
    return {
      additionalTerms,
      template: selectedTemplate,
      realtor: {
        ownerFullName: effectiveUser.ownerFullName,
        ownerPhoneNumber: effectiveUser.ownerPhoneNumber,
        email: effectiveUser.email,
        companyName: effectiveUser.companyName,
        companyLogoUrl: effectiveUser.companyLogoUrl,
        companyId: effectiveUser.companyId,
        signatureImageUrl: effectiveUser.signatureImageUrl,
        realtorLicense: effectiveUser.realtorLicense
      },
      contact: manualSignerMode
        ? { contactName: signerName, phoneNumber: signerPhone }
        : buildContactContext(primarySigner),
      listings: effectiveListings,
      // Preview document number: the server allocates the real number (and
      // bumps the per-user counter) at send time — but for the preview we
      // want the realtor to see the shape the formatted number will take.
      // Uses the typed override when the realtor edited the number field,
      // else the fetched per-user next; EMPTY while the counter is still
      // loading or failed (never a made-up value). Replaced with the real
      // number on send.
      // EDIT MODE keeps the document's allocated number — no new allocation.
      documentFormattedNumber: editDocument
        ? editDocument.formattedNumber
        : effectiveNextDocumentNumber === null
        ? ""
        : `${selectedTemplateUserCounter.numberPrefix ?? ""}${effectiveNextDocumentNumber}`,
      // Use a synthetic single-signer record so the renderer can pull
      // sender-typed values from fieldValues.
      signers: [
        {
          id: "preview-signer",
          roleId: role?.id || "primary",
          name: signerName,
          phone: signerPhone,
          email: signerEmail,
          fieldValues,
          status: "pending",
          whatsappDelivery: { status: "pending", attemptCount: 0 }
        }
      ],
      signerRoleNameById: Object.fromEntries(selectedTemplate.signerRoles.map((r) => [r.id, r.name])),
      fieldOverrides,
      // LOCKED signer prefills bake into the document (no signer input).
      // Editable seeds deliberately stay OUT of the context — they ride
      // signers[].fieldValues at create/edit time instead (see onSend).
      lockedSignerValues,
      hideAddresses: effectiveHideAddresses
    };
  }, [
    selectedTemplate,
    selectedTemplateUserCounter,
    effectiveNextDocumentNumber,
    userSettings,
    effectiveUser,
    primarySigner,
    manualSignerMode,
    signerName,
    signerPhone,
    signerEmail,
    effectiveListings,
    fieldValues,
    fieldOverrides,
    lockedSignerValues,
    effectiveHideAddresses,
    termsTitle,
    termsHtml,
    termsEmpty,
    editDocument
  ]);

  // True when the selection can consume send-time additional terms — the
  // selected template, or ANY bundle member, carries a terms block. Gates
  // the additional-terms section of the form.
  const hasAdditionalTermsBlock = useMemo(
    () =>
      selectedBundle
        ? bundleTemplates.some((tp) => !!tp.additionalTermsBlock)
        : !!selectedTemplate?.additionalTermsBlock,
    [selectedBundle, bundleTemplates, selectedTemplate]
  );

  // True when the picked template has at least one listings block — the
  // realtor must pick listings for the block to expand into something
  // meaningful at send time. Without this gate we'd ship documents with an
  // empty table.
  const needsListing = useMemo(
    () => !!selectedTemplate?.listingsBlocks?.length,
    [selectedTemplate]
  );
  // True when at least one field on the template references a listing.*
  // prefill source (address / price / rooms / square_meter). Combined with
  // needsListing above, this drives the "show listing picker at all" gate.
  const usesListingFields = useMemo(
    () =>
      !!selectedTemplate?.fields.some(
        (f) => f.prefillSource && f.prefillSource.startsWith("listing.")
      ),
    [selectedTemplate]
  );
  // Show the listings card only if the template will actually consume them.
  const showListingPicker = needsListing || usesListingFields;
  // A listings block can explicitly restrict the send to ONE listing (the
  // template-editor flag; prerequisite for the seller questionnaire). In
  // bundle mode ANY bundled template carrying the flag restricts the shared
  // picker — the listing selection is bundle-wide.
  const blockRestrictsSingleListing = useMemo(
    () =>
      !!selectedTemplate?.listingsBlocks?.some((b) => b.restrictToSingleListing) ||
      bundleTemplates.some((tp) => tp.listingsBlocks?.some((b) => b.restrictToSingleListing)),
    [selectedTemplate, bundleTemplates]
  );
  // Single-listing mode: either the block says so explicitly, or there's no
  // block (no need for many) but at least one listing.* field exists, so the
  // picker should accept exactly one.
  const singleListingMode = blockRestrictsSingleListing || (!needsListing && usesListingFields);

  // Which listings the shared picker offers, from the listings-block
  // `listingFilter` across the selected template + any bundled templates. The
  // rule (a 'both' block narrows and widens nothing; sale + rent widens) lives
  // in `pickerListingFilter`.
  // A questionnaire-only document is ABOUT the role's property: picked as a seller's it is a sale,
  // as a landlord's a rental (the template itself takes either, for the AI and manual picks).
  const questionnaireOnlySelected = !selectedBundle && isQuestionnaireOnlyDocument(selectedTemplate);
  const listedForFilter = useMemo<ListingsBlockListingFilter>(
    () =>
      questionnaireOnlySelected && questionnaireOnly && (signRole === "seller" || signRole === "landlord")
        ? signRole === "landlord"
          ? "rent"
          : "sale"
        : pickerListingFilter([
            ...(selectedTemplate?.listingsBlocks ?? []),
            ...bundleTemplates.flatMap((tp) => tp.listingsBlocks ?? [])
          ]),
    [selectedTemplate, bundleTemplates, questionnaireOnlySelected, questionnaireOnly, signRole]
  );
  // Names the questionnaire (a rental's is שאלון בעל זכות בנכס) here and, sent
  // with it, on every screen after.
  const questionnaireListedFor = questionnaireListedForAtSend(selectedListings[0], listedForFilter);

  // True when a listings block opts into the statutory seller questionnaire
  // (שאלון מוכר) — sent as a post-signing task alongside the document.
  const templateHasSellerQuestionnaire = (tp?: SignatureTemplate | null): boolean => templateCarriesQuestionnaire(tp);
  const sendsSellerQuestionnaire =
    templateHasSellerQuestionnaire(selectedTemplate) || bundleTemplates.some((tp) => templateHasSellerQuestionnaire(tp));
  // WHICH contract of a set carries the questionnaire, by the name the client reads (Trello #231,
  // item 8 follow-up: Haim looked for it on the exclusivity contract of a seller pair).
  const questionnaireCarrierTitles = questionnaireCarrierTitlesOf(selectedBundle ? bundleTemplates : []);
  // The questionnaire goes out with the picked property's address — never without one: a listing with
  // no address used to drop the questionnaire silently while this form said it was attached.
  const questionnaireAddressMissing = isQuestionnaireAddressMissing(
    sendsSellerQuestionnaire,
    selectedListings.length,
    effectiveListings[0]
  );

  // Enforce single-listing cap whenever the mode kicks in. A template
  // change that flips from block→fields-only would otherwise leave a
  // multi-listing selection in place.
  useEffect(() => {
    if (singleListingMode && selectedListings.length > 1) {
      setSelectedListings(selectedListings.slice(0, 1));
    }
    if (!showListingPicker && selectedListings.length > 0) {
      setSelectedListings([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleListingMode, showListingPicker]);

  // Deep-link listing preselect (initialListingIds). Applied ONCE and only
  // after the resolved template actually shows the listings picker — applying
  // earlier would be wiped by the "template shows no picker" reset above.
  // Never runs in edit mode (the document's own listingIds win) and never
  // stomps listings the user already picked by hand.
  const pendingInitialListingIdsRef = useRef<string[] | null>(
    initialListingIds && initialListingIds.length > 0 ? [...initialListingIds] : null
  );
  useEffect(() => {
    if (!showListingPicker || editDocument) return;
    const ids = pendingInitialListingIdsRef.current;
    if (!ids || ids.length === 0) return;
    pendingInitialListingIdsRef.current = null;
    void Promise.all(ids.map((id) => fetchListingById(id).catch(() => null))).then(
      (listings) => {
        const resolved = listings.filter(Boolean) as ProcessedListingData[];
        if (resolved.length === 0) return;
        // Respect the single-listing cap here too: this resolves async, AFTER
        // the cap effect above has already run for this template, so a deep
        // link carrying several ids into a restrictToSingleListing template
        // would otherwise leave a multi-listing selection that nothing trims.
        const capped = singleListingMode ? resolved.slice(0, 1) : resolved;
        setSelectedListings((prev) => (prev.length > 0 ? prev : capped));
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showListingPicker, editDocument]);

  // ---- Resolve a prefilled field's current value from context ----
  // Single source of truth used by BOTH the missing-fields detection (a
  // resolved value means "not missing") AND the field-values override
  // card (used as the input's placeholder so the realtor sees what would
  // be sent without an override). user_info_block is per-sub-line in the
  // missing-detection logic and isn't routed here — keep this resolver
  // simple and call it per individual source.
  const resolveSourceValue = (
    field: SignatureTemplateField
  ): string => {
    const src = field.prefillSource;
    if (!src || src === "none") return "";
    const c: any = primarySigner || {};
    // For listing.* sources we read from the post-override context, so the
    // realtor's edits in the per-listing override card surface as the
    // placeholder for any field bound to listing.*.
    const el = effectiveListings[0];
    const u: any = effectiveUser || {};
    switch (src) {
      case "today":
        return new Date().toLocaleDateString("he-IL");
      case "document.formatted_number":
        return ""; // resolved at send time
      case "contact.name":
        return getFullName(c.callerFirstName, c.callerLastName);
      case "contact.phone":
        return c.id || "";
      case "contact.email":
        return c.mail || "";
      case "contact.id_number":
        return c.tehudatZehut || "";
      case "contact.additional_phones":
        return (c.additionalPhoneNumbers || []).filter(Boolean).join(", ");
      case "signing_date":
        return ""; // stamped by us at signing — never a realtor-typed value
      case "contact.address":
        return c.address || "";
      case "listing.address":
        // Mirror the renderer's `listing.address` resolution
        // (signaturesHtmlSend.ts) — fall back to street + houseNumber + city
        // when the listing has no `fullAddress`. Without this fallback the
        // override placeholder reads as blank for plenty of listings that
        // DO have street parts, surprising the realtor.
        if (!el) return "";
        if (el.address) return el.address;
        return [el.street, el.houseNumber].filter(Boolean).join(" ").trim() ||
          el.city || "";
      case "listing.price":
        return el?.price != null ? String(el.price) : "";
      case "listing.rooms":
        return el?.rooms != null ? String(el.rooms) : "";
      case "listing.square_meter":
        return el?.squareMeter != null ? String(el.squareMeter) : "";
      case "realtor.name":
        return u.ownerFullName || "";
      case "realtor.phone":
        return u.ownerPhoneNumber || "";
      case "realtor.email":
        return u.email || "";
      case "realtor.company":
        return u.companyName || "";
      case "realtor.license":
        return u.realtorLicense || "";
      case "realtor.company_id":
        return u.companyId || "";
      case "realtor.company_logo":
        return u.companyLogoUrl || "";
      case "realtor.signature_image":
        return u.signatureImageUrl || "";
      case "realtor.user_info_block":
        // Compound: not editable as a single string in the override card.
        // Per-sub-line missing detection handles its breakdown elsewhere.
        return "";
      default:
        return "";
    }
  };

  // Sources the realtor can override per-document via the field-values
  // card. ONE entry per unique source so a source referenced by N fields
  // doesn't render N inputs — they all share one value anyway.
  //
  // Excluded: sender-typed (own section), realtor.* (own card), compound
  // user_info_block (no single string), `today` and
  // `document.formatted_number` (auto-resolved at send time — surfacing
  // them as overridable confused realtors into thinking they had to fill
  // the document number themselves), and image-typed sources (no
  // text-input override makes sense).
  type OverridableSource = {
    source: SignaturePrefillSource;
    /** Representative field — used for friendly-name lookup + sample
     *  field.name to display alongside the override input. */
    sampleField: SignatureTemplateField;
    /** True when ANY field referencing this source is required. */
    required: boolean;
  };
  const overridableSources = useMemo<OverridableSource[]>(() => {
    if (!selectedTemplate) return [];
    const out: OverridableSource[] = [];
    const idxBySrc = new Map<SignaturePrefillSource, number>();
    for (const f of selectedTemplate.fields) {
      if (effectiveFillMode(f) !== "prefilled") continue;
      const src = f.prefillSource;
      if (!src || src === "none") continue;
      if (src === "today" || src === "document.formatted_number") continue;
      if (src === "realtor.user_info_block") continue;
      if (src.startsWith("realtor.")) continue;
      // listing.* sources are edited per-listing in the dedicated overrides
      // card below the listings picker, not as a single per-source input —
      // a "Listing price" field in a multi-listing block can't be one value
      // shared across rows, so we route those edits through `listingOverrides`.
      if (src.startsWith("listing.")) continue;
      if (f.type === "image") continue;
      const existingIdx = idxBySrc.get(src);
      if (existingIdx === undefined) {
        idxBySrc.set(src, out.length);
        out.push({ source: src, sampleField: f, required: !!f.required });
      } else if (f.required) {
        out[existingIdx].required = true;
      }
    }
    return out;
  }, [selectedTemplate]);

  // ---- Missing-fields detection ----
  // Walks every prefilled field and flags those whose source can't resolve
  // from the current realtor / contact / listing context. Powers the
  // warning panel below and the per-input red highlight in the realtor
  // card. user_info_block is enumerated per enabled sub-line so each
  // missing realtor field shows up as its own warning row.
  type MissingPrefillEntry = {
    fieldName: string;
    label: string;
    userKey?: keyof User;
    /** Cannot be bypassed (currently only realtor.signature_image). */
    blocking?: boolean;
  };
  const missingPrefillFields = useMemo<MissingPrefillEntry[]>(() => {
    if (!selectedTemplate) return [];
    const c: any = primarySigner || {};
    // Use the post-override listing context so an overridden value counts
    // as "not missing" — otherwise the realtor types an address in the
    // override card and the warning panel still nags them about it.
    const el = effectiveListings[0];
    const u: any = effectiveUser || {};
    const missing: MissingPrefillEntry[] = [];
    const seenUserKeys = new Set<keyof User>();
    // Dedupe per source. Three drops of the contact_name tile share the
    // same prefillSource ("contact.name") and resolve to the same value;
    // a missing source means ALL its fields are missing, but the realtor
    // only needs ONE warning row per source — repeating the same line
    // for "contact_name", "contact_name_2", "contact_name_3" was noisy.
    const seenSources = new Set<SignaturePrefillSource>();
    const pushMissing = (entry: MissingPrefillEntry) => {
      if (entry.userKey) {
        if (seenUserKeys.has(entry.userKey)) return;
        seenUserKeys.add(entry.userKey);
      }
      missing.push(entry);
    };

    for (const f of selectedTemplate.fields) {
      if (effectiveFillMode(f) !== "prefilled") continue;
      // Optional prefilled fields are allowed to render blank — the realtor
      // marked them non-required intentionally, so a missing source value is
      // not a problem worth surfacing.
      if (!f.required) continue;
      const src = f.prefillSource;
      // Sender-typed (no source), today, signing date, document number all
      // resolve automatically at send/sign time — exclude from the warning
      // list. `signing_date` in particular is always stamped by the system
      // when the ceremony completes, never by the realtor or signer, so a
      // blank value is expected and must not nag.
      if (
        !src ||
        src === "none" ||
        src === "today" ||
        src === "signing_date" ||
        src === "document.formatted_number"
      )
        continue;
      // Already processed this source via an earlier field — skip the
      // re-resolve and the duplicate warning push. user_info_block stays
      // outside this dedup since it's enumerated per sub-line, not per source.
      if (src !== "realtor.user_info_block" && seenSources.has(src)) continue;
      // Per-source override fully replaces the resolved value — if it's
      // non-empty, every field referencing that source will render and
      // none of them are missing.
      if (fieldOverrides[src]?.trim()) {
        seenSources.add(src);
        continue;
      }

      if (src === "realtor.user_info_block") {
        const cfg = f.userInfoBlockConfig;
        for (const k of USER_INFO_BLOCK_LINE_KEYS) {
          const enabled = cfg?.[k]?.enabled ?? true;
          if (!enabled) continue;
          const userKey = USER_INFO_BLOCK_PROFILE_KEY[k];
          if (u[userKey]) continue;
          pushMissing({
            fieldName: f.name,
            label: USER_INFO_BLOCK_LABEL[k],
            userKey
          });
        }
        continue;
      }

      // Resolve the source against the current context — same logic the
      // renderer uses, just inline so we can detect "blank" without
      // constructing a full SendRenderContext for each field.
      let resolved: any = "";
      switch (src) {
        case "contact.name":
          resolved = getFullName(c.callerFirstName, c.callerLastName);
          break;
        case "contact.phone":
          resolved = c.id || "";
          break;
        case "contact.email":
          resolved = c.mail || "";
          break;
        case "contact.id_number":
          resolved = c.tehudatZehut || "";
          break;
        case "contact.additional_phones":
          resolved = (c.additionalPhoneNumbers || []).filter(Boolean).join(", ");
          break;
        case "contact.address":
          resolved = c.address || "";
          break;
        case "listing.address":
          resolved = el?.address ||
            [el?.street, el?.houseNumber].filter(Boolean).join(" ").trim() ||
            el?.city ||
            "";
          break;
        case "listing.price":
          resolved = el?.price ?? "";
          break;
        case "listing.rooms":
          resolved = el?.rooms ?? "";
          break;
        case "listing.square_meter":
          resolved = el?.squareMeter ?? "";
          break;
        case "realtor.name":
          resolved = u.ownerFullName || "";
          break;
        case "realtor.phone":
          resolved = u.ownerPhoneNumber || "";
          break;
        case "realtor.email":
          resolved = u.email || "";
          break;
        case "realtor.company":
          resolved = u.companyName || "";
          break;
        case "realtor.license":
          resolved = u.realtorLicense || "";
          break;
        case "realtor.company_id":
          resolved = u.companyId || "";
          break;
        case "realtor.company_logo":
          resolved = u.companyLogoUrl || "";
          break;
        case "realtor.signature_image":
          resolved = u.signatureImageUrl || "";
          break;
        default:
          resolved = "";
      }
      seenSources.add(src);
      if (resolved !== "" && resolved != null) continue;
      pushMissing({
        fieldName: f.name,
        label: getFieldFriendlyName(src) || src,
        userKey: REALTOR_SOURCE_TO_USER_KEY[src],
        // No realtor signature image = no real signed document. Hard block.
        blocking: src === "realtor.signature_image"
      });
    }
    return missing;
  }, [selectedTemplate, primarySigner, effectiveListings, effectiveUser, fieldOverrides]);

  /** User keys highlighted red in the realtor-details card. */
  const missingUserKeys = useMemo(() => {
    const set = new Set<keyof User>();
    for (const m of missingPrefillFields) if (m.userKey) set.add(m.userKey);
    return set;
  }, [missingPrefillFields]);

  const hasBlockingMissing = useMemo(
    () => missingPrefillFields.some((m) => m.blocking),
    [missingPrefillFields]
  );

  // Sender-typed required-empty groups. Surfaced in the same warning
  // panel as missing prefill sources so the realtor sees a single
  // consolidated list of "values you still need to provide" — without
  // this, an empty required text field showed an asterisk on its input
  // but never made it into the warning card. Keyed by linkGroup so a
  // duplicated text field shows up once.
  const missingSenderTypedGroups = useMemo<MissingPrefillEntry[]>(() => {
    const out: MissingPrefillEntry[] = [];
    for (const g of senderTypedGroups) {
      if (!g.required) continue;
      const v = (fieldValues[g.key] || "").trim();
      if (v) continue;
      const friendly =
        getFieldFriendlyName(g.field.prefillSource) ||
        getFieldFriendlyName(g.field.labelKey) ||
        g.field.name;
      out.push({ fieldName: g.key, label: friendly });
    }
    return out;
  }, [senderTypedGroups, fieldValues]);

  // Address parts the picked listings still owe us. A document that names a
  // property has to name it unambiguously — "Herzl St, Tel Aviv" identifies a
  // street, not a flat — so a listing missing its house number (or its
  // apartment number, for property types that have one) gates the send just
  // like any other unfilled value. Which parts are required per property type
  // is decided by the same helper the listing-details cards render from, so
  // the warning list and the red inputs can never disagree.
  const missingListingAddressParts = useMemo<MissingPrefillEntry[]>(() => {
    if (!showListingPicker) return [];
    const out: MissingPrefillEntry[] = [];
    for (const entry of getListingsCompleteness(selectedListings, listingOverrides)) {
      for (const part of entry.missingParts) {
        out.push({
          fieldName: `listingAddress:${entry.listingId}:${part}`,
          label: t("signatures.newDoc.listingDetails.missingFieldRow", {
            field: addressPartLabel(part, t),
            listing: listingShortLabel(entry, t)
          }) as string
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showListingPicker, selectedListings, listingOverrides, t]);

  /** Combined missing-fields list rendered in the warning card and used
   *  by the send-time gate. */
  const allMissingFields = useMemo<MissingPrefillEntry[]>(
    () => [...missingPrefillFields, ...missingSenderTypedGroups, ...missingListingAddressParts],
    [missingPrefillFields, missingSenderTypedGroups, missingListingAddressParts]
  );

  // Optional fields that the customer will fill while signing. We surface
  // these in an info card so the realtor knows in advance that those slots
  // may come back blank — the signing canvas lets the signer skip them
  // (required is set to false on the field tag, and an email is never
  // required of the signer: isSignatureFieldRequiredForSigner). Dedup by
  // linkGroupId so duplicated fields render as one row.
  type OptionalSignerEntry = { key: string; label: string };
  const optionalSignerFields = useMemo<OptionalSignerEntry[]>(() => {
    if (!selectedTemplate) return [];
    const seen = new Set<string>();
    const out: OptionalSignerEntry[] = [];
    for (const f of selectedTemplate.fields) {
      if (effectiveFillMode(f) !== "signer") continue;
      if (isSignatureFieldRequiredForSigner(f)) continue;
      // Signing date is always stamped by the system when the ceremony
      // completes — never filled or skipped by the signer, so it must not
      // appear in the "optional fields the signer may skip" card.
      if (f.prefillSource === "signing_date") continue;
      // A group the realtor already pre-filled (signer-prefill card) is no
      // longer "left for the signer" — the signer opens it with a value.
      if ((signerPrefillValues[f.linkGroupId || f.name] ?? "").trim()) continue;
      const key = f.linkGroupId || f.id;
      if (seen.has(key)) continue;
      seen.add(key);
      const label = f.signerLabel?.trim() || f.name;
      out.push({ key, label });
    }
    return out;
  }, [selectedTemplate, signerPrefillValues]);

  // Inputs that have a typed value but fail their per-type validator
  // (Israeli T.Z. checksum, email shape, Israeli phone, numeric). We keep
  // this separate from `allMissingFields` because the failure mode is
  // different: missing → "you forgot to fill it" (bypassable), invalid →
  // "what you typed is wrong, fix it before send" (NOT bypassable). Empty
  // values are deliberately skipped here so the asterisk + missing-fields
  // panel handle required-but-empty without us double-flagging.
  type InvalidEntry = { key: string; label: string; error: FieldValidationError };
  const invalidFieldEntries = useMemo<InvalidEntry[]>(() => {
    const out: InvalidEntry[] = [];
    for (const o of overridableSources) {
      const v = (fieldOverrides[o.source] ?? "").trim();
      if (!v) continue;
      const err = validateFieldValue(o.sampleField, v);
      if (!err) continue;
      const label =
        getFieldFriendlyName(o.source) ||
        getFieldFriendlyName(o.sampleField.labelKey) ||
        o.source;
      out.push({ key: `override:${o.source}`, label, error: err });
    }
    for (const g of senderTypedGroups) {
      const v = (fieldValues[g.key] ?? "").trim();
      if (!v) continue;
      const err = validateFieldValue(g.field, v);
      if (!err) continue;
      const label =
        getFieldFriendlyName(g.field.prefillSource) ||
        getFieldFriendlyName(g.field.labelKey) ||
        g.field.name;
      out.push({ key: `typed:${g.key}`, label, error: err });
    }
    // Signer-prefill seeds get the same treatment: a non-empty seed that
    // fails its validator (bad email / failing T.Z. checksum) blocks send —
    // shipping an invalid default to the signer just moves the error to the
    // worst possible moment (mid-ceremony).
    for (const g of signerPrefillGroups) {
      const v = (signerPrefillValues[g.key] ?? "").trim();
      if (!v) continue;
      const err = validateFieldValue(g.field, v);
      if (!err) continue;
      out.push({ key: `signerPrefill:${g.key}`, label: signerPrefillLabel(g.field), error: err });
    }
    return out;
  }, [
    overridableSources,
    fieldOverrides,
    senderTypedGroups,
    fieldValues,
    signerPrefillGroups,
    signerPrefillValues
  ]);

  /**
   * Explicit user opt-in to send despite missing source-bound values. Reset
   * whenever the missing-fields list changes so the realtor has to re-confirm
   * after fixing one issue (or surfacing a new one).
   */
  const [bypassMissingFields, setBypassMissingFields] = useState(false);
  // Reset signature includes sender-typed entries too — typing a value
  // into a required text field is one of the ways the realtor "fixes
  // one issue", and we want them to re-confirm bypass when the missing
  // list changes for any reason.
  const missingSignature = allMissingFields.map((m) => m.fieldName).join("|");
  useEffect(() => {
    setBypassMissingFields(false);
  }, [missingSignature]);

  // Preview and send compile the same resolved HTML. PDF field placeholders
  // are drawn from the returned manifest, without a separate preview layout.
  const previewHtml = useMemo<string | null>(() => {
    if (!selectedTemplate || !sendContext || !selectedTemplate.content?.html) return null;
    try {
      return renderHtmlForSend(selectedTemplate.content.html, {
        ...sendContext,
        mode: "send"
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Preview render failed", err);
      return null;
    }
  }, [selectedTemplate, sendContext]);

  /**
   * One bundle member's render context — the ONE definition behind its
   * preview, its compile-ahead and its send, so all three resolve to the same
   * HTML and the send can reuse the compilation. Projects the merged form
   * values onto THIS template's fields (matched by name: the form input came
   * from the first occurrence carrying that name+role) and stamps the member's
   * own number — the typed override when set, else that template's fetched
   * next (numbering is per-template). `sendContext` carries the MERGED
   * template's number, which is wrong for every member but the first. Empty
   * when the batch counter fetch failed: the server still allocates the real
   * per-template number for the stored document.
   */
  const bundleMemberContext = (tp: SignatureTemplate, base: SendRenderContext): SendRenderContext => {
    const tplFieldValues: Record<string, string> = {};
    for (const f of tp.fields || []) {
      if (fieldValues[f.name] !== undefined) tplFieldValues[f.name] = fieldValues[f.name];
    }
    return {
      ...base,
      template: tp,
      documentFormattedNumber: bundleCountersState === "ready" ? effectiveBundleMemberFormattedNumber(tp.id) : "",
      // signerFieldValues mirror tplFieldValues so the same prefills appear in the rendered HTML.
      signers: base.signers.map((s) => ({ ...s, fieldValues: tplFieldValues })),
      signerRoleNameById: Object.fromEntries(tp.signerRoles.map((r) => [r.id, r.name])),
      mode: "send"
    };
  };

  // Bundle preview: render each bundle template into its own HTML so the
  // preview dialog can swap between them via a dropdown. Same context as the
  // bundle send (`bundleMemberContext`); the dialog compiles the active member
  // into a PDF. `null` outside bundle mode → dialog falls back to the
  // single-doc `previewHtml` path.
  const bundlePreviewDocs = useMemo<Array<{ id: string; label: string; html: string }> | null>(() => {
    if (!selectedBundle || bundleTemplates.length === 0 || !sendContext) return null;
    const docs: Array<{ id: string; label: string; html: string }> = [];
    for (const tp of bundleTemplates) {
      if (!tp.content?.html) continue;
      try {
        docs.push({
          id: tp.id,
          label: tp.customerFacingName || tp.name,
          html: renderHtmlForSend(tp.content.html, bundleMemberContext(tp, sendContext))
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Bundle template preview render failed", tp.id, err);
      }
    }
    return docs.length > 0 ? docs : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBundle, bundleTemplates, sendContext, fieldValues, bundleCounters, bundleNumberInputs, bundleCountersState]);

  const validate = (): string | null => {
    // The role row says documents that are not (all) selected yet: one is still
    // loading, or failed to load. Never prepare the selection in the meantime.
    if (roleState.block) return t(roleDocumentsBlockKey(roleState.block)) as string;
    if (!selectedTemplate) return t("signatures.newSignatureForm.errors.noTemplate") as string;
    if (!selectedTemplate.content?.html) return t("signatures.newSignatureForm.errors.emptyTemplate") as string;
    if (manualSignerMode) {
      if (!signerName) {
        return t("signatures.newSignatureForm.errors.manualSignerNameRequired") as string;
      }
      if (!signerPhone) {
        return t("signatures.newSignatureForm.errors.manualSignerPhoneRequired") as string;
      }
      if (!isValidPhone(signerPhone)) {
        return t("signatures.newSignatureForm.errors.manualSignerPhoneInvalid") as string;
      }
    } else {
      if (!primarySigner) return t("signatures.newSignatureForm.errors.noSigner") as string;
      if (!primarySigner.id) {
        return t("signatures.newSignatureForm.errors.signerNoPhone") as string;
      }
    }
    // Listings-block templates can't be sent with an empty listings array —
    // the rendered table would be empty and the realtor would notice only
    // after the document is signed.
    if (needsListing && selectedListings.length === 0) {
      return (questionnaireOnlySelected
        ? t("signatures.newSignatureForm.errors.noListingQuestionnaire")
        : t("signatures.newSignatureForm.errors.noListings")) as string;
    }
    // Hard block, not bypassable: the questionnaire cannot go out without the property's address,
    // and dropping it while the form says it is attached is the silent failure this replaces.
    if (questionnaireAddressMissing) {
      return t("signatures.newSignatureForm.errors.questionnaireNoAddress", {
        context: questionnaireLabelContext(questionnaireListedFor)
      }) as string;
    }
    // Hard block — realtor.signature_image missing means no actual stamp
    // on the signed document. Cannot be bypassed.
    if (hasBlockingMissing) {
      return t("signatures.newDoc.missingSignatureBlockTitle") as string;
    }
    // Soft block — explicit acknowledgement before shipping a doc with
    // unresolved values. Covers both unresolved prefill sources AND
    // sender-typed required-empty groups (see allMissingFields). The
    // realtor sees the consolidated list in the warning panel below
    // and can opt-in via the bypass checkbox.
    if (allMissingFields.length > 0 && !bypassMissingFields) {
      return t("signatures.newDoc.missingFieldsTitle") as string;
    }
    // Hard block — typed values that don't match their type's validator
    // (bad email, bad Israeli phone, bad T.Z., non-numeric). Different
    // from missing: bypassing "missing" means "send blank, I know"; there
    // is no equivalent "send invalid, I know" — invalid values would be
    // stamped onto the document or rejected by our validator at sign time.
    if (invalidFieldEntries.length > 0) {
      return t("signatures.newDoc.validation.title") as string;
    }
    // Document-number overrides: a typed value that parses to < 1 blocks
    // send; an empty field simply falls back to the auto number. Bundle mode
    // has one editable number per member template.
    if (!selectedBundle && documentNumberInvalid) {
      return t("signatures.newSignatureForm.documentNumberInvalid") as string;
    }
    if (selectedBundle && anyBundleNumberInvalid) {
      return t("signatures.newSignatureForm.documentNumberInvalid") as string;
    }
    return null;
  };

  /**
   * The addresses addendum of a hidden-addresses send. The FULL addresses come
   * from the post-override listing contexts (`fullAddress` — the realtor may
   * have corrected them in the override card), rendered as a listings table
   * mirroring the template's block; the document number is the one stamped on
   * the main document so the addendum unambiguously references it. Compiled
   * through the same pipeline as the document, alongside it.
   */
  const buildAddendumHtml = (template: SignatureTemplate, documentFormattedNumber: string): string => {
    const fullListings = effectiveListings.map((l) => ({
      ...l,
      address: l.fullAddress || l.address
    }));
    const block =
      template.listingsBlocks?.find((b) => b.allowHiddenAddresses) ??
      template.listingsBlocks?.[0] ??
      null;
    return renderAddressesAddendumHtml({
      documentTitle: template.customerFacingName?.trim() || template.name,
      documentFormattedNumber,
      listings: fullListings,
      block,
      signerRoleName: template.signerRoles?.[0]?.name || "Signer"
    });
  };
  const addendumPayload = (html: string, rendered: HtmlToPdfResult): REST_schema.SignatureSendAddendum => ({
    html,
    pdfBase64: rendered.pdfBase64,
    fields: rendered.fields,
    totalPages: rendered.totalPages
  });

  /**
   * Everything one click will compile, in send order: each contract, then
   * the addenda (the contracts are longer and decide the wait). Single and
   * edit sends render with the number they expect — the predicted next (or the
   * edited document's own); the click re-renders with the allocated number,
   * and only a different number means a different document to compile.
   * `null` when nothing can be rendered yet.
   */
  const buildPreparationDocuments = (
    numberForSingle: string
  ): { members: Array<{ template: SignatureTemplate; html: string; addendumHtml: string | null }>; items: CompileItem[] } | null => {
    if (!selectedTemplate || !sendContext) return null;
    let members: Array<{ template: SignatureTemplate; html: string; addendumHtml: string | null }>;
    if (selectedBundle && bundleTemplates.length > 0) {
      members = bundleTemplates.map((tp) => {
        if (!tp.content?.html) throw new Error(`Template "${tp.name}" has no content yet`);
        const ctx = bundleMemberContext(tp, sendContext);
        // Hidden-addresses sends attach the addresses addendum to every
        // bundled doc that actually renders listings (has a listings block) —
        // those are the docs whose tables were redacted. The redaction itself
        // applies bundle-wide via the shared listing contexts, so no template
        // in the bundle leaks the full address before signature.
        const addendumHtml =
          effectiveHideAddresses && (tp.listingsBlocks?.length ?? 0) > 0
            ? buildAddendumHtml(tp, ctx.documentFormattedNumber || "")
            : null;
        return { template: tp, html: renderHtmlForSend(tp.content.html, ctx), addendumHtml };
      });
    } else {
      if (!selectedTemplate.content?.html) return null;
      members = [
        {
          template: selectedTemplate,
          html: renderHtmlForSend(selectedTemplate.content.html, {
            ...sendContext,
            documentFormattedNumber: numberForSingle,
            mode: "send"
          }),
          addendumHtml: effectiveHideAddresses ? buildAddendumHtml(selectedTemplate, numberForSingle) : null
        }
      ];
    }
    const items: CompileItem[] = [
      ...members.map((member) => ({ html: member.html })),
      ...members.flatMap((member) => (member.addendumHtml ? [{ html: member.addendumHtml, addendum: true }] : []))
    ];
    return { members, items };
  };

  // ---- Compile ahead ----
  // While the form is ready to send, compile exactly what the click would send.
  // Realtors spend 24 s (p10) to 109 s (p50) in this form before preparing, and
  // a preparation that finds its documents compiled takes ~1.4 s instead of
  // 7–13 s. Only a ready form (the send button enabled) and only once the form
  // has been still for a moment; a single send needs a known number (a counter
  // that failed to load stamps the allocated one at the click instead).
  //
  // The previews already hold the documents' HTML (same contexts as the
  // click: `previewHtml` is the single document at the expected number,
  // `bundlePreviewDocs` the members), so nothing is rendered twice per keystroke.
  const readyToPrepare = !sending && !validate();
  const aheadHtmls = useMemo<string[] | null>(() => {
    if (!SIGNATURE_PREPARE_AHEAD_ENABLED || !readyToPrepare || !sendContext) return null;
    try {
      if (selectedBundle && bundleTemplates.length > 0) {
        if (!bundlePreviewDocs || bundlePreviewDocs.length !== bundleTemplates.length) return null;
        const addenda = effectiveHideAddresses
          ? bundleTemplates
              .filter((tp) => (tp.listingsBlocks?.length ?? 0) > 0)
              .map((tp) => buildAddendumHtml(tp, bundleMemberContext(tp, sendContext).documentFormattedNumber || ""))
          : [];
        return [...bundlePreviewDocs.map((doc) => doc.html), ...addenda];
      }
      if (!previewHtml || !selectedTemplate || !sendContext.documentFormattedNumber) return null;
      return effectiveHideAddresses
        ? [previewHtml, buildAddendumHtml(selectedTemplate, sendContext.documentFormattedNumber)]
        : [previewHtml];
    } catch {
      return null;
    }
    // previewHtml / bundlePreviewDocs change exactly when the documents do; the
    // addendum reads the listings (effectiveListings) and the expected number.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToPrepare, previewHtml, bundlePreviewDocs, effectiveHideAddresses, effectiveListings, sendContext]);
  useEffect(() => {
    if (!aheadHtmls || aheadHtmls.length === 0) return undefined;
    const timer = window.setTimeout(() => prepareAhead.request(aheadHtmls), PREPARE_AHEAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [aheadHtmls, prepareAhead]);

  /**
   * Seller-questionnaire prefill for the send payload: the picked listing's
   * FULL address (post-override; never the redacted one), else street + house
   * number (`questionnairePropertyAddress`), + its property type. The server
   * snapshots the realtor details from the sender's profile and computes the
   * per-signer owner prefill at signing time. Called only for a template that
   * carries the questionnaire, and NEVER returns without it: `validate()`
   * already refuses a property with no address, and should a send get here
   * anyway it fails with that message rather than go out without the
   * questionnaire the form promised.
   */
  const buildSellerQuestionnairePayload = (): REST_schema.SignatureSendSellerQuestionnaire => {
    const address = questionnairePropertyAddress(effectiveListings[0]);
    if (!address) {
      throw new Error(
        t("signatures.newSignatureForm.errors.questionnaireNoAddress", {
          context: questionnaireLabelContext(questionnaireListedFor)
        }) as string
      );
    }
    const propertyType = selectedListings[0]?.propertyType;
    return {
      property: { address, ...(propertyType ? { propertyType } : {}) },
      listedFor: questionnaireListedFor
    };
  };

  /**
   * Snapshot of the send-form inputs that only exist baked into the rendered
   * HTML (terms, per-source and per-listing overrides) — persisted on the
   * document so a later edit-after-send can prefill the form losslessly.
   * Undefined when nothing worth snapshotting was entered. Entries are
   * cleaned of empty values so Firestore never sees `undefined`.
   */
  const buildSendFormSnapshot = (): SignatureSendFormSnapshot | undefined => {
    const additionalTerms =
      !termsEmpty && termsHtml.trim() ? { title: termsTitle, html: termsHtml } : undefined;
    const cleanFieldOverrides = Object.fromEntries(
      Object.entries(fieldOverrides).filter(([, v]) => typeof v === "string" && v !== "")
    ) as Record<string, string>;
    const cleanListingOverrides = Object.fromEntries(
      Object.entries(listingOverrides).filter(([, v]) => v && Object.values(v).some((x) => x !== undefined && x !== ""))
    );
    // Signer prefills: value + the "allow signer to edit" decision per group,
    // so edit-after-send restores the checkbox (a locked value is baked into
    // the HTML and otherwise unrecoverable as form state).
    const signerPrefills: Record<string, { value: string; allowEdit: boolean }> = {};
    for (const [key, value] of Object.entries(nonEmptySignerPrefills)) {
      signerPrefills[key] = { value, allowEdit: !signerPrefillLocked[key] };
    }
    const snapshot: SignatureSendFormSnapshot = {
      ...(additionalTerms ? { additionalTerms } : {}),
      ...(Object.keys(cleanFieldOverrides).length > 0 ? { fieldOverrides: cleanFieldOverrides } : {}),
      ...(Object.keys(cleanListingOverrides).length > 0 ? { listingOverrides: cleanListingOverrides } : {}),
      ...(Object.keys(signerPrefills).length > 0 ? { signerPrefills } : {})
    };
    return Object.keys(snapshot).length > 0 ? snapshot : undefined;
  };

  const onSend = async () => {
    if (preparingRef.current) return;
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    if (!selectedTemplate || !sendContext) return;

    preparingRef.current = true;
    onBusyChange?.(true);
    setSending(true);
    setError(null);

    // Keep the form open until the archived contract and signing link exist —
    // sharing is a separate, explicit action in the prepared-contract dialog —
    // and show the realtor what is happening meanwhile (accepted, step, time left).
    const isBundle = !!selectedBundle && bundleTemplates.length > 0;
    const kind: PreparationKind = isBundle ? "bundle" : editDocument ? "edit" : "single";
    // The number this click expects to stamp: the edited document's own, else
    // the predicted next (a single send re-renders with the allocated one).
    const expectedNumber = editDocument ? editDocument.formattedNumber : sendContext.documentFormattedNumber;
    let expected: ReturnType<typeof buildPreparationDocuments> = null;
    try {
      expected = buildPreparationDocuments(expectedNumber);
    } catch {
      // A member that cannot render fails the preparation below, with its message.
    }
    const documentCount = expected?.members.length ?? 1;
    setProgress(startPreparation(kind, documentCount, expected?.items ?? []));
    const compileOptions = {
      onCompiled: (index: number) => {
        if (index < documentCount) {
          setProgress((p) => p && { ...p, compiledCount: Math.min(p.documentCount, p.compiledCount + 1), retrying: false });
        }
      },
      onBusy: () => setProgress((p) => p && { ...p, retrying: true })
    };

    try {
      // Let the preparing state paint before compilation begins.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      // The questionnaire's prefill, built BEFORE anything reaches the server: a send that cannot carry
      // the questionnaire it announced stops here, with nothing created.
      const sellerQuestionnairePayload = sendsSellerQuestionnaire ? buildSellerQuestionnairePayload() : undefined;
      const sellerQuestionnaireFor = (tp: SignatureTemplate | null): REST_schema.SignatureSendSellerQuestionnaire | undefined =>
        templateHasSellerQuestionnaire(tp) ? sellerQuestionnairePayload : undefined;
      const role = selectedTemplate.signerRoles[0];
      // Fill-if-empty write-back: persist realtor-typed contact field values
      // (email / id-number / extra phones) onto the contact — only filling
      // fields the contact is currently missing, never overwriting. Through the server's
      // update, which never creates: a signer who is not a contact stays not one.
      // Best-effort and AFTER the contract is ready: a failure here must not
      // surface as a send failure, and the realtor does not wait for it.
      const writeBackContactFieldsFromOverrides = async () => {
        if (!primarySigner?.id) return;
        const patch: Partial<RealEstateContact> = {};
        const mailOverride = fieldOverrides["contact.email"]?.trim();
        if (mailOverride && !primarySigner.mail?.trim()) patch.mail = mailOverride;
        const idOverride = fieldOverrides["contact.id_number"]?.trim();
        if (idOverride && !primarySigner.tehudatZehut?.trim()) patch.tehudatZehut = idOverride;
        const phonesOverride = fieldOverrides["contact.additional_phones"]?.trim();
        if (phonesOverride && !primarySigner.additionalPhoneNumbers?.length) {
          patch.additionalPhoneNumbers = phonesOverride
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);
        }
        if (Object.keys(patch).length === 0) return;
        try {
          await updateContact(primarySigner.id, patch);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("Signature contact write-back failed (non-fatal):", e);
        }
      };

      if (isBundle) {
        // === BUNDLE SEND ===
        // Every member renders with the user-counter's preview number (the
        // server allocates the real number when it creates the doc — minor
        // mismatch possible if another doc was created between fetch and
        // send, but the PDF/send flow is unaffected and the discrepancy is
        // only in the cosmetic number stamp). The members and their addenda
        // compile side by side: one after the other, a seller + exclusivity
        // pair waited for two full compiles.
        const docs = buildPreparationDocuments("");
        if (!docs) throw new Error(t("signatures.newSignatureForm.errors.emptyTemplate"));
        setProgress((p) => p && enterCompileStep(p, docs.items));
        const compiled = await compileDocuments(docs.items.map((item) => item.html), compileOptions);
        setProgress((p) => p && enterSaveStep(p));
        let addendumIndex = docs.members.length;
        const perDocument: REST_schema.SendSignatureBundleRequest["perDocument"] = docs.members.map((member, index) => {
          const tp = member.template;
          const rendered = compiled[index];
          const addendum = member.addendumHtml ? addendumPayload(member.addendumHtml, compiled[addendumIndex++]) : undefined;
          const memberOverride = parseBundleMemberOverride(tp.id);
          const memberNext = bundleCounters[tp.id]?.nextDocumentNumber ?? 1;
          return {
            templateId: tp.id,
            html: member.html,
            pdfBase64: rendered.pdfBase64,
            fields: rendered.fields,
            totalPages: rendered.totalPages,
            hideAddresses: addendum ? true : undefined,
            addendum,
            sellerQuestionnaire: sellerQuestionnaireFor(tp),
            // Only an actual change is sent as an override — an untouched
            // (seeded) value keeps normal auto-allocation, so a number that
            // went stale between open and send can't be double-issued.
            documentNumberOverride:
              bundleCountersState === "ready" && memberOverride !== null && memberOverride !== memberNext
                ? memberOverride
                : undefined
          };
        });
        // Ad-hoc groups (a role + its exclusivity) have no persisted bundle — ship
        // the template list and let the server mint the grouping id. Saved
        // bundles keep sending by id.
        const isAdHocBundle = selectedBundle!.id === ADHOC_BUNDLE_ID;
        const bundleSendResult = await sendBundleApi({
          ...(isAdHocBundle
            ? {
                adHoc: {
                  templateIds: bundleTemplates.map((tp) => tp.id).filter(Boolean) as string[],
                  name: selectedBundle!.name
                }
              }
            : { bundleId: selectedBundle!.id }),
          contactIds: primarySigner?.id && !manualSignerMode ? [primarySigner.id] : [],
          listingIds: selectedListings.map((l) => l.id).filter(Boolean) as string[],
          signers: [
            {
              roleId: role?.id || "primary",
              contactId: manualSignerMode ? undefined : primarySigner?.id,
              name: signerName,
              phone: signerPhone,
              email: signerEmail,
              fieldValues: tplFieldValuesPlaceholderForCreate(fieldValues, bundleTemplates)
            }
          ],
          notes,
          otpRequired: enforceOtp,
          notifySigners: false,
          perDocument
        });
        const failed = bundleSendResult.perDocumentResults.filter((item) => item.status === "failed");
        const preparedDocuments = bundleSendResult.documents.filter((item) => item.status === "sent" && item.sentPdfUrl);
        if (!preparedDocuments.length) throw new Error(t("signatures.prepared.allFailed"));
        onSent({ bundleId: bundleSendResult.bundleId, documents: preparedDocuments, failedDocumentCount: failed.length });
        void writeBackContactFieldsFromOverrides();
        return;
      }

      if (editDocument) {
        // === EDIT-RESEND (sent, unsigned document) ===
        // No draft creation and no number allocation — the document keeps its
        // identity (id, number, signing slugs). We re-render with the SAME
        // formatted number, compile it (and its addendum, side by side), and
        // ship through the edit endpoint, which swaps the sent artifacts in place.
        const docs = buildPreparationDocuments(editDocument.formattedNumber);
        if (!docs) throw new Error(t("signatures.newSignatureForm.errors.emptyTemplate"));
        setProgress((p) => p && enterCompileStep(p, docs.items));
        const [rendered, addendumRendered] = await compileDocuments(docs.items.map((item) => item.html), compileOptions);
        setProgress((p) => p && enterSaveStep(p));
        const member = docs.members[0];
        const prepared = await editSentDocument(editDocument.id, {
          pdfBase64: rendered.pdfBase64,
          fields: rendered.fields,
          totalPages: rendered.totalPages,
          html: member.html,
          hideAddresses: effectiveHideAddresses || undefined,
          addendum: member.addendumHtml ? addendumPayload(member.addendumHtml, addendumRendered) : undefined,
          sellerQuestionnaire: sellerQuestionnaireFor(selectedTemplate),
          otpRequired: enforceOtp,
          notifySigners: false,
          sendFormSnapshot: buildSendFormSnapshot(),
          signers: [
            {
              id: editDocument.signers[0].id,
              contactId: manualSignerMode ? undefined : primarySigner?.id,
              name: signerName,
              phone: signerPhone,
              email: signerEmail,
              fieldValues: { ...fieldValues, ...nonEmptySignerPrefills }
            }
          ],
          notes,
          contactIds: primarySigner?.id && !manualSignerMode ? [primarySigner.id] : [],
          listingIds: selectedListings.map((l) => l.id).filter(Boolean) as string[]
        });
        onSent({ documentId: prepared.id, documents: [prepared] });
        void writeBackContactFieldsFromOverrides();
        return;
      }

      // === SINGLE-DOC SEND ===

      // 0) Start compiling the document at the number it expects while that
      //    number is allocated. The compile cache is keyed by the resolved
      //    HTML, so step 3 reuses this compilation only when the allocated
      //    number is the expected one; otherwise it compiles the right one.
      //    (Usually it was compiled ahead, while the form sat ready.)
      if (expected && expectedNumber) {
        void compileDocuments(expected.items.map((item) => item.html)).catch(() => undefined);
      }

      // 1) Create the draft document. The typed document number is sent as
      //    an override only when it differs from the fetched next — leaving
      //    the seeded value untouched keeps normal auto-allocation (immune
      //    to the displayed number going stale if another document was sent
      //    in the meantime).
      const documentNumberOverride =
        counterFetchState === "ready" &&
        overrideDocumentNumber !== null &&
        overrideDocumentNumber !== selectedTemplateUserCounter.nextDocumentNumber
          ? overrideDocumentNumber
          : undefined;
      const draftInput = {
        templateId: selectedTemplate.id,
        contactIds: primarySigner?.id && !manualSignerMode ? [primarySigner.id] : [],
        listingIds: selectedListings.map((l) => l.id).filter(Boolean) as string[],
        signers: [
          {
            roleId: role?.id || "primary",
            contactId: manualSignerMode ? undefined : primarySigner?.id,
            name: signerName,
            phone: signerPhone,
            email: signerEmail,
            // Signer-prefill seeds ride the same stored map — the public
            // signing payload echoes signer.fieldValues as prefilledValues,
            // seeding the sign page's inputs. Editable seeds are deliberately
            // NOT threaded into the render context above; only the LOCKED
            // subset reaches it (ctx.lockedSignerValues), where the signer
            // branch bakes the value and drops the field tag — so a locked
            // field never reaches sentFields and the signer can't submit it.
            // Locked values still ride this map too: harmless to the ceremony
            // (no matching field tag → the seed is skipped) and it keeps the
            // stored record of what the sender supplied complete.
            fieldValues: { ...fieldValues, ...nonEmptySignerPrefills }
          }
        ],
        notes,
        documentNumberOverride
      };
      // A preparation retry reuses its draft and number. If the previous
      // response was lost after success, recover the already prepared contract.
      let existing: SignatureDocument | null = null;
      if (pendingDraftRef.current?.templateId === selectedTemplate.id) {
        existing = await getDocument(pendingDraftRef.current.id);
        if (existing.status !== "draft") {
          if (existing.sentPdfUrl && existing.signers[0]?.signing?.publicSigningUrl) {
            onSent({ documentId: existing.id, documents: [existing] });
            return;
          }
          throw new Error(t("signatures.newSignatureForm.errors.sendFailed"));
        }
      }
      const created = existing ? await updateDocument(existing.id, {
        contactIds: draftInput.contactIds,
        listingIds: draftInput.listingIds,
        notes: draftInput.notes,
        signers: draftInput.signers.map((signer, index) => ({ ...existing!.signers[index], ...signer }))
      }) : await createDocument(draftInput);
      pendingDraftRef.current = created;

      // 2) Render the HTML using the document's freshly-allocated number —
      //    and, for a hidden-addresses send, the addresses addendum (title,
      //    doc number, disclosure sentence, FULL addresses, signature), which
      //    the signer sees only AFTER signing the main document.
      const docs = buildPreparationDocuments(created.formattedNumber);
      if (!docs) throw new Error(t("signatures.newSignatureForm.errors.emptyTemplate"));
      const member = docs.members[0];

      // 3) Compile PDF pixels and field geometry together on the server, the
      //    document and its addendum side by side. The sender's browser never
      //    determines the document's layout.
      setProgress((p) => p && enterCompileStep(p, docs.items));
      const [rendered, addendumRendered] = await compileDocuments(docs.items.map((item) => item.html), compileOptions);

      // 4) Ship PDF + extracted fields to the backend. `html` is sent too so
      //    the server can keep the `sentHtml` debug snapshot intact.
      setProgress((p) => p && enterSaveStep(p));
      const prepared = await sendDocument(created.id, {
        pdfBase64: rendered.pdfBase64,
        fields: rendered.fields,
        totalPages: rendered.totalPages,
        html: member.html,
        hideAddresses: effectiveHideAddresses || undefined,
        addendum: member.addendumHtml ? addendumPayload(member.addendumHtml, addendumRendered) : undefined,
        sellerQuestionnaire: sellerQuestionnaireFor(selectedTemplate),
        otpRequired: enforceOtp,
        notifySigners: false,
        sendFormSnapshot: buildSendFormSnapshot()
      });

      onSent({ documentId: prepared.id, documents: [prepared] });
      void writeBackContactFieldsFromOverrides();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("Send failed", err);
      const failed = t("signatures.newSignatureForm.errors.sendFailed", {
        context: questionnaireOnlySelected ? "questionnaire" : undefined
      }) as string;
      setError(err?.response?.data?.error || err?.message || failed);
      enqueueSnackbar(
        err?.response?.data?.error || err?.message || failed,
        { variant: "error" }
      );
    } finally {
      setProgress(null);
      preparingRef.current = false;
      setSending(false);
      onBusyChange?.(false);
    }
  };

  /**
   * Build a union signer.fieldValues map for the bundle's CREATE call. The
   * server stores this on each created doc's signer record (one map per doc,
   * keyed by THAT doc's field names). We pass the union so every doc gets
   * the values it needs without the client having to compute per-doc maps a
   * second time (the per-template perDocument[] payload already contains
   * the per-template values inlined into the rendered HTML).
   */
  function tplFieldValuesPlaceholderForCreate(
    formFieldValues: Record<string, string>,
    tpls: SignatureTemplate[]
  ): Record<string, string> {
    const out: Record<string, string> = {};
    for (const tp of tpls) {
      for (const f of tp.fields || []) {
        if (formFieldValues[f.name] !== undefined) out[f.name] = formFieldValues[f.name];
      }
    }
    // Signer-prefill seeds ride the same union map — the server stores it on
    // every created doc's signer record, and the public signing payload
    // echoes it back as `prefilledValues`. Keyed per MEMBER template by the
    // tag name ITS rendered field uses (`linkGroupId || name`), so members
    // that carry the field under a different key than the merged view's
    // representative still get seeded (fallback matches by contact source).
    // These keys belong to signer-mode fields, which the renderer never
    // resolves from fieldValues — and the render contexts in the send loop
    // don't receive them anyway.
    for (const tp of tpls) {
      for (const f of tp.fields || []) {
        if (effectiveFillMode(f) !== "signer") continue;
        if (
          f.type === "signature" ||
          f.type === "initials" ||
          f.type === "image" ||
          f.type === "checkbox" ||
          f.type === "price"
        )
          continue;
        if ((f.prefillSource ?? f.labelKey) === "signing_date") continue;
        const source = resolveSignerPrefillSource(f);
        const tagKey = f.linkGroupId || f.name;
        if (out[tagKey] !== undefined) continue;
        const group =
          signerPrefillGroups.find((g) => g.key === tagKey) ??
          (source ? signerPrefillGroups.find((g) => g.source === source) : undefined);
        const seed = group ? nonEmptySignerPrefills[group.key] : undefined;
        if (seed !== undefined) out[tagKey] = seed;
      }
    }
    return out;
  }

  return (
    // Two-region layout: a scrollable body + a fixed action footer BELOW the
    // scroll area. The host DialogContent renders with p:0/overflow:hidden so
    // this component owns both scrolling and padding — the footer can never
    // overlap form content (the previous `position: sticky` bar floated
    // INSIDE the scroll container and form content visibly slid underneath
    // it).
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box
        component="fieldset"
        disabled={sending}
        sx={{
          border: 0,
          m: 0,
          minWidth: 0,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: { xs: 2, md: 3 },
          // Every section keeps its natural height and overflows into the
          // scroll above — without this they collapse. This column has a
          // definite height (flex child of a fixed-height dialog), so its
          // children are flex items that shrink by default, and MUI's Card
          // sets `overflow: hidden`, which makes `min-height: auto` resolve
          // to 0 instead of min-content — i.e. nothing stops a card from
          // shrinking to zero.
          "& > *": { flexShrink: 0 }
        }}
      >
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {editDocument && (
          <Alert severity="info">
            {t("signatures.newSignatureForm.editModeBanner", {
              number: editDocument.formattedNumber
            })}
            {/* Docs sent before the send-form snapshot existed can't restore
                send-time terms/overrides — tell the realtor to re-check. */}
            {!editDocument.sendFormSnapshot && hasAdditionalTermsBlock && (
              <>
                {" "}
                {t("signatures.newSignatureForm.editModeNoSnapshotWarning")}
              </>
            )}
          </Alert>
        )}

      <Card sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("signatures.newSignatureForm.templateLabel")}
        </Typography>

        {editDocument ? (
          // EDIT MODE: the template is fixed — the document's identity
          // (number, snapshots, signing slugs) is bound to it. Show a locked
          // summary instead of the pickers.
          <Stack direction="row" spacing={1} alignItems="center">
            {selectedTemplate ? (
              <>
                <TemplateOriginBadge template={selectedTemplate} size={28} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <TemplateNameTag nameTag={selectedTemplate.nameTag} />
                  {selectedTemplate.name}
                  {` · ${editDocument.formattedNumber}`}
                </Typography>
              </>
            ) : (
              <CircularProgress size={20} />
            )}
          </Stack>
        ) : (
        <>
        {/* Quick pick: who's signing? Selecting a role auto-picks the default
            template configured for that scenario; "custom" reveals the classic
            template/bundle dropdown below. */}
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <ToggleButtonGroup
            value={signRole}
            exclusive
            size="small"
            color="primary"
            onChange={(_, next) => handleRoleChange(next)}
            sx={{ flexWrap: "wrap" }}
          >
            {SIGNATURE_QUICK_PICK_ROLES.map((role) => (
              <ToggleButton key={role} value={role}>
                {t(`signatures.newSignatureForm.rolePicker.${role}`)}
              </ToggleButton>
            ))}
            <ToggleButton value="custom">
              {t("signatures.newSignatureForm.rolePicker.custom")}
            </ToggleButton>
          </ToggleButtonGroup>
          {questionnaireOnlyAvailable && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={questionnaireOnly}
                  onChange={(e) => setQuestionnaireOnly(e.target.checked)}
                />
              }
              label={t("signatures.newSignatureForm.rolePicker.questionnaireOnlyToggle") as string}
            />
          )}
          {exclusivityAvailable && !questionnaireOnly && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={withExclusivity}
                  onChange={(e) => setWithExclusivity(e.target.checked)}
                />
              }
              label={t("signatures.newSignatureForm.rolePicker.exclusiveToggle") as string}
            />
          )}
          {roleState.noDefault && signRole && signRole !== "custom" && (
            <Alert severity="info">
              {t("signatures.newSignatureForm.rolePicker.noDefaultForRole")}
            </Alert>
          )}
        </Stack>

        {loadingTemplates ? (
          <CircularProgress size={20} />
        ) : signRole && signRole !== "custom" && !roleState.noDefault ? (
          // Role-resolved selection: the document(s) this role sends, listed
          // the same way whether it's one template or several (the role +
          // its exclusivity). "custom" switches back to the full dropdown.
          roleState.listed.length > 0 ? (
            <Stack spacing={1}>
              {roleState.listed.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  {t("signatures.newSignatureForm.bundleBannerDocCount_other", {
                    count: roleState.listed.length
                  })}
                </Typography>
              )}
              {roleState.listed.map((tp) => (
                <Stack key={tp.id} direction="row" spacing={1} alignItems="center">
                  <TemplateOriginBadge template={tp} size={28} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      <TemplateNameTag nameTag={tp.nameTag} />
                      {tp.name}
                    </Typography>
                    {/* WHICH document brings the questionnaire (a seller pair: the order, never the
                        exclusivity). A questionnaire on its own IS one: nothing to add. */}
                    {templateHasSellerQuestionnaire(tp) && !isQuestionnaireOnlyDocument(tp) && (
                      <QuestionnaireRidesHere listedFor={questionnaireListedFor} sx={{ mt: 0.25 }} />
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : (
            // Nothing of the role's selected yet: a spinner while it loads, and
            // once it failed, says so (the note above the button has "נסו שוב").
            <RoleDocumentsListPlaceholder block={roleState.block} />
          )
        ) : (
          <Autocomplete<TemplatePickerOption, false, false, false>
            value={pickerValue}
            onChange={(_, v) => handlePickerChange(v)}
            options={pickerOptions}
            getOptionLabel={(o) => (o.kind === "bundle" ? o.bundle.name : o.template.name)}
            // Group headers only earn their vertical space when there are
            // actually bundles to separate from the plain templates.
            groupBy={
              bundles.length > 0
                ? (o) =>
                    o.kind === "bundle"
                      ? (t("signatures.newSignatureForm.pickerGroupBundles") as string)
                      : (t("signatures.newSignatureForm.pickerGroupTemplates") as string)
                : undefined
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("signatures.newSignatureForm.templatePlaceholder") as string}
                InputProps={{
                  ...params.InputProps,
                  // Show the picked template's name tag on the SELECTED value
                  // too (not only in the dropdown options), as a start adornment
                  // before the name text. Bundles carry no tag.
                  startAdornment: (
                    <>
                      {pickerValue?.kind === "template" && pickerValue.template.nameTag ? (
                        <TemplateNameTag nameTag={pickerValue.template.nameTag} />
                      ) : null}
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
            isOptionEqualToValue={(a, b) => a.optionId === b.optionId}
            renderOption={(props, option) => {
              // Pull `key` out of the spread props because React 17 forbids
              // spreading it; MUI v5 includes it in the props bag.
              const { key, ...liProps } = props as typeof props & { key?: React.Key };
              return (
                <Box
                  component="li"
                  key={key}
                  {...liProps}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    py: 1
                  }}
                >
                  {option.kind === "bundle" ? (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "primary.lighter",
                        color: "primary.main",
                        flexShrink: 0
                      }}
                    >
                      <LayersIcon sx={{ fontSize: 18 }} />
                    </Box>
                  ) : (
                    <TemplateOriginBadge template={option.template} size={28} />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {option.kind === "bundle" ? (
                        option.bundle.name
                      ) : (
                        <>
                          <TemplateNameTag nameTag={option.template.nameTag} />
                          {option.template.name}
                        </>
                      )}
                    </Typography>
                    {option.kind === "bundle" ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.3, mt: 0.25 }}
                      >
                        {(() => {
                          // Hand-rolled plural — i18next v20 default doesn't
                          // auto-resolve _one/_other in this project.
                          const count = option.bundle.templateIds.length;
                          const suffix = count === 1 ? "_one" : "_other";
                          return t(`signatures.newSignatureForm.bundleBannerDocCount${suffix}`, { count });
                        })()}
                        {option.bundle.description ? ` · ${option.bundle.description}` : ""}
                      </Typography>
                    ) : (
                      option.template.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", lineHeight: 1.3, mt: 0.25 }}
                        >
                          {option.template.description}
                        </Typography>
                      )
                    )}
                  </Box>
                </Box>
              );
            }}
          />
        )}
        </>
        )}
      </Card>

      {/* Bundle banner — confirms the composition about to ship (edited from
          /signatures/bundles, not here). Shown below the picker so the
          realtor can still switch to another bundle/template above.
          Skipped for the ad-hoc seller+exclusivity pair: the quick-pick above
          already lists those documents, so a second card would just repeat
          them under a "bundle" label the user never opted into. */}
      {selectedBundle && selectedBundle.id !== ADHOC_BUNDLE_ID && (
        <Card sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {t("signatures.newSignatureForm.bundleBannerTitle", "Sending bundle")}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedBundle.name}
              {selectedBundle.customerFacingName ? ` · ${selectedBundle.customerFacingName}` : ""}
            </Typography>
            {bundleLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    // Hand-rolled plural — see SignPage's welcomeIntroMulti
                    // (i18next v20 default doesn't auto-resolve _one/_other).
                    const count = bundleTemplates.length;
                    const suffix = count === 1 ? "_one" : "_other";
                    return t(`signatures.newSignatureForm.bundleBannerDocCount${suffix}`, { count });
                  })()}
                </Typography>
                {/* Names only — the per-document numbers (and their overrides)
                    live in the "document number" card at the END of the form,
                    same position as the single-template flow. */}
                {bundleTemplates.map((tp, idx) => (
                  <Box key={tp.id}>
                    <Typography variant="caption" color="text.secondary">
                      {idx + 1}. <TemplateNameTag nameTag={tp.nameTag} />
                      {tp.name}
                    </Typography>
                    {templateHasSellerQuestionnaire(tp) && !isQuestionnaireOnlyDocument(tp) && (
                      <QuestionnaireRidesHere listedFor={questionnaireListedFor} />
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      )}

        <Card sx={{ p: 2 }}>
          {manualSignerMode ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t("signatures.newSignatureForm.manualSignerTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                {t("signatures.newSignatureForm.manualSignerHelper")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  id="signature-manual-signer-name"
                  size="small"
                  label={t("signatures.newSignatureForm.manualSignerName") as string}
                  value={manualSignerName}
                  onChange={(e) => setManualSignerName(e.target.value)}
                  required
                  fullWidth
                  helperText={t("signatures.newSignatureForm.manualSignerNameHelper") as string}
                />
                <TextField
                  id="signature-manual-signer-phone"
                  size="small"
                  type="tel"
                  label={t("signatures.newSignatureForm.manualSignerPhone") as string}
                  value={manualSignerPhone}
                  onChange={(e) => setManualSignerPhone(e.target.value)}
                  required
                  fullWidth
                  inputProps={{ dir: "ltr", inputMode: "tel" }}
                  error={!!manualSignerPhone.trim() && !isValidPhone(manualSignerPhone)}
                  helperText={
                    manualSignerPhone.trim() && !isValidPhone(manualSignerPhone)
                      ? (t("signatures.newSignatureForm.errors.manualSignerPhoneInvalid") as string)
                      : (t("signatures.newSignatureForm.manualSignerPhoneHelper") as string)
                  }
                />
              </Stack>
              {visibleSignerPrefillGroups.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <SignerPrefillInputs
                    compact
                    groups={visibleSignerPrefillGroups}
                    values={signerPrefillValues}
                    locked={signerPrefillLocked}
                    onValueChange={(key, value) => setSignerPrefillValues((prev) => ({ ...prev, [key]: value }))}
                    onLockChange={(key, value) => setSignerPrefillLocked((prev) => ({ ...prev, [key]: value }))}
                  />
                </Box>
              )}
            </>
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">
                  {t("signatures.newSignatureForm.contactLabel")}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setContactDialogOpen(true)}
                >
                  {t("signatures.newSignatureForm.createContact")}
                </Button>
              </Stack>
              <NewContactPicker
                selectedContact={primarySigner}
                setSelectedContact={setPrimarySigner}
              />
            </>
          )}

          {/* ── A COLLEAGUE IS ALREADY RUNNING THIS PERSON ──────────────────────────────
              Inline and non-blocking, WHILE the form is still being filled. The confirm
              dialog fires at the entry point that opened this form; this is the reminder
              for a signer chosen here, and it is the level at which something can still be
              done about it cheaply.

              ⚠️ ITS ACTION IS THE BEST ESCAPE HATCH IN THE PRODUCT, AND IT ALREADY EXISTS.
              The org-admin "send under another agent's identity" control (`OrgUsersPicker`)
              is in the very next card. When Dana handles this lead the right move is usually
              not "send anyway" — it is "send as Dana", which is one click from here. Offered
              only to admins, because that picker already is. */}
          {!manualSignerMode && collidingSignerHandler && (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              icon={<CrmMemberAvatar name={collidingSignerHandler.name} avatarUrl={collidingSignerHandler.avatarUrl} size={22} />}
              action={
                isAdminOfTheOrganization ? (
                  <Button size="small" sx={{ fontWeight: 800 }} onClick={() => setPickedUserEmail(collidingSignerHandler.userId)}>
                    {`שלח בשם ${collidingSignerHandler.name}`}
                  </Button>
                ) : undefined
              }
            >
              {`${collidingSignerHandler.name} מטפל/ת בלקוח הזה מטעם המשרד.`}
            </Alert>
          )}
        </Card>

        {/* Inline contact creation. On success the new contact is auto-selected
            as the signer (the freeSolo picker renders a value not in its options). */}
        {!manualSignerMode && (
          <EditContactDialog
            open={contactDialogOpen}
            propertyTypes={propertyTypes}
            forceCreation
            onClose={() => setContactDialogOpen(false)}
            onSuccess={(created) => {
              if (created) setPrimarySigner(created as RealEstateContact);
              setContactDialogOpen(false);
            }}
          />
        )}

        {/* Realtor details — collapsed editor + admin user picker. The inputs
             default to the current realtor's saved profile (shown as
             placeholders). Any tweak here applies ONLY to this document and
             is never written back to userSettings. Org admins additionally
             see the user picker for sending under another org user's identity. */}
        {selectedTemplate && (
          <Card sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setRealtorSectionOpen((v) => !v)}
              sx={{
                cursor: "pointer",
                userSelect: "none",
                borderRadius: 1,
                p: 0.5,
                m: -0.5,
                "&:hover": { bgcolor: "action.hover" }
              }}
            >
              <Typography variant="subtitle2">
                {t("signatures.newDoc.realtorDetailsTitle")}
              </Typography>
              <Button
                size="small"
                variant="text"
                component="div"
                endIcon={
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: realtorSectionOpen ? "rotate(180deg)" : "none",
                      transition: "transform 150ms ease"
                    }}
                  />
                }
                sx={{ textTransform: "none", pointerEvents: "none" }}
              >
                {realtorSectionOpen
                  ? t("signatures.newDoc.realtorDetailsCollapse")
                  : t("signatures.newDoc.realtorDetailsExpand")}
              </Button>
            </Stack>
            <Collapse in={realtorSectionOpen} unmountOnExit>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5, mb: 1.5 }}
              >
                {t("signatures.newDoc.realtorDetailsHelper")}
              </Typography>
              <Stack spacing={1.5}>
                {isAdminOfTheOrganization && (
                  <OrgUsersPicker
                    value={pickedUserEmail}
                    onChange={setPickedUserEmail}
                    fullWidth
                  />
                )}
                <TextField
                  size="small"
                  label={t("signatures.newDoc.realtorName") as string}
                  value={overrideValue("ownerFullName")}
                  placeholder={overridePlaceholder("ownerFullName")}
                  onChange={(e) => setOverride("ownerFullName", e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={missingUserKeys.has("ownerFullName")}
                  helperText={
                    missingUserKeys.has("ownerFullName")
                      ? (t("signatures.newDoc.realtorFieldRequired") as string)
                      : undefined
                  }
                />
                <TextField
                  size="small"
                  type="tel"
                  label={t("signatures.newDoc.realtorPhone") as string}
                  value={overrideValue("ownerPhoneNumber")}
                  placeholder={overridePlaceholder("ownerPhoneNumber")}
                  onChange={(e) => setOverride("ownerPhoneNumber", e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={missingUserKeys.has("ownerPhoneNumber")}
                  helperText={
                    missingUserKeys.has("ownerPhoneNumber")
                      ? (t("signatures.newDoc.realtorFieldRequired") as string)
                      : undefined
                  }
                />
                <TextField
                  size="small"
                  label={t("signatures.newDoc.realtorCompany") as string}
                  value={overrideValue("companyName")}
                  placeholder={overridePlaceholder("companyName")}
                  onChange={(e) => setOverride("companyName", e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={missingUserKeys.has("companyName")}
                  helperText={
                    missingUserKeys.has("companyName")
                      ? (t("signatures.newDoc.realtorFieldRequired") as string)
                      : undefined
                  }
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    size="small"
                    label={t("signatures.newDoc.realtorLicense") as string}
                    value={overrideValue("realtorLicense")}
                    placeholder={overridePlaceholder("realtorLicense")}
                    onChange={(e) => setOverride("realtorLicense", e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={missingUserKeys.has("realtorLicense")}
                    helperText={
                      missingUserKeys.has("realtorLicense")
                        ? (t("signatures.newDoc.realtorFieldRequired") as string)
                        : undefined
                    }
                  />
                  <TextField
                    size="small"
                    label={t("signatures.newDoc.realtorCompanyId") as string}
                    value={overrideValue("companyId")}
                    placeholder={overridePlaceholder("companyId")}
                    onChange={(e) => setOverride("companyId", e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={missingUserKeys.has("companyId")}
                    helperText={
                      missingUserKeys.has("companyId")
                        ? (t("signatures.newDoc.realtorFieldRequired") as string)
                        : undefined
                    }
                  />
                </Stack>
              </Stack>
            </Collapse>
          </Card>
        )}

        {/* Listings card — only shown when the template will actually
             consume listings. Three sub-modes:
               - listings block present  → multi-select, required
               - listing.* fields only   → single-select (the fields read
                                            from selectedListings[0])
               - neither                 → card hidden entirely */}
        {showListingPicker && (
          <Card sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  flex: { sm: 1 },
                  color:
                    needsListing && selectedListings.length === 0
                      ? "error.main"
                      : undefined
                }}
              >
                {needsListing && singleListingMode
                  ? t("signatures.newSignatureForm.listingsLabelSingleRequired")
                  : needsListing
                  ? t("signatures.newSignatureForm.listingsLabelRequired")
                  : singleListingMode
                  ? t("signatures.newSignatureForm.listingsLabelSingle")
                  : t("signatures.newSignatureForm.listingsLabel")}
              </Typography>
              {/* Actions sit on their own row that can wrap — on a narrow
                  phone the long "הוספה מנכסים במיניסייט" button otherwise
                  collides with the label and the create-listing button. */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: "wrap", flexShrink: 0 }}
              >
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setListingDialogOpen(true)}
                >
                  {t("signatures.newSignatureForm.createListing")}
                </Button>
                {primarySigner?.id && (
                  <FavoriteListingsModalButton
                    contactId={primarySigner.id}
                    contactName={getFullName(
                      primarySigner.callerFirstName,
                      primarySigner.callerLastName
                    )}
                    selectedListings={selectedListings}
                    onAddListings={(additions) => {
                      const merged = [...selectedListings, ...additions];
                      // Mirror the dropdown's single-listing-mode cap so a
                      // bulk pick from favorites doesn't bypass the
                      // "one listing only" constraint enforced by the picker.
                      if (singleListingMode) {
                        setSelectedListings(merged.slice(-1));
                      } else {
                        setSelectedListings(merged);
                      }
                    }}
                  />
                )}
              </Stack>
            </Stack>
            <NewListingPicker
              loading={false}
              closeOnSelect={singleListingMode}
              listedForFilter={listedForFilter}
              selectedListings={selectedListings}
              onSelectedListings={(next) =>
                singleListingMode
                  ? setSelectedListings(next.slice(-1))
                  : setSelectedListings(next)
              }
            />

            {/* Hidden-addresses toggle — only for templates whose listings
                block supports it. The template enables the CAPABILITY; the
                decision itself is per-send, made right here where the realtor
                picks the listings. */}
            {templateAllowsHiddenAddresses && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: hideAddresses ? "warning.light" : "divider",
                  bgcolor: hideAddresses ? "rgba(255,193,7,0.08)" : "transparent"
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideAddresses}
                      onChange={(e) => setHideAddresses(e.target.checked)}
                    />
                  }
                  label={t("signatures.newDoc.hideAddressesLabel") as string}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4 }}>
                  {t("signatures.newDoc.hideAddressesHelper")}
                </Typography>
              </Box>
            )}

            {/* Seller-questionnaire notice — the template opted in, so the
                picked listing's full address is prefilled into the statutory
                שאלון מוכר the signer fills AFTER signing. The flag lives on the
                template, not per-send. It says WITH WHICH contract it goes (a
                set names it: only the order carries it, never the exclusivity)
                and WHEN the client meets it (right after signing that
                contract, on the same link) — Trello #231, item 8 follow-up. */}
            {/* The two key choices stay HERE, as they always were: this component is so large that
                eslint-plugin-react-hooks 4.2 loses count of its paths, and adding or removing a branch
                in its body turns dozens of hooks into false "called conditionally" errors. */}
            {sendsSellerQuestionnaire && (
              <SellerQuestionnaireNotice
                titleKey={
                  questionnaireOnlySelected
                    ? "signatures.newDoc.questionnaireOnlyNoteTitle"
                    : "signatures.newDoc.sellerQuestionnaireNoteTitle"
                }
                bodyKey={
                  questionnaireOnlySelected
                    ? "signatures.newDoc.questionnaireOnlyNoteBody"
                    : "signatures.newDoc.sellerQuestionnaireNoteBody"
                }
                carrierTitles={questionnaireCarrierTitles}
                listedFor={questionnaireListedFor}
                addressMissing={questionnaireAddressMissing}
              />
            )}

            {/* Inline listing creation (webot listing). On creation the new
                listing is injected + auto-selected immediately — the picker keeps
                selected items even before Algolia indexes the new record. */}
            <NewListingDialog
              open={listingDialogOpen}
              onClose={() => setListingDialogOpen(false)}
              onConfirm={() => setListingDialogOpen(false)}
              onListingCreation={(_listingId, _isExclusivitySet, createdListing) => {
                if (createdListing) {
                  const next = [...selectedListings, createdListing as ProcessedListingData];
                  setSelectedListings(singleListingMode ? next.slice(-1) : next);
                }
                setListingDialogOpen(false);
              }}
            />

            {/* Per-document listing details. Unlike the old collapsed
                "overrides" drawer this is always on screen once a listing is
                picked: a contract has to identify its property, and scraped
                listings routinely arrive without a house or apartment number.
                Complete listings collapse to a single line, so the section only
                asks for attention where something is actually missing. Edits
                never write back to the listing record. */}
            <ListingDetailsSection
              listings={selectedListings}
              overrides={listingOverrides}
              onOverrideChange={setListingOverride}
              hideAddresses={effectiveHideAddresses}
              currentUserEmail={(userSettings as any)?.id}
              onListingRecordUpdated={applyListingRecordUpdate}
            />
          </Card>
        )}

        {/* Document values — single merged section that combines:
             1. Per-source override inputs for prefilled-with-source fields
                (placeholder shows the resolved value). One input per unique
                source — fields sharing a source share an input.
             2. Sender-typed inputs for fields the realtor types fresh per
                send. One input per linkGroup — duplicated/linked fields
                share an input.
             Realtor.* + auto-resolved sources (today, document number) live
             elsewhere or fill themselves so they're not in this list.
             Required fields (`required` on the underlying schema) get the
             MUI asterisk via the `required` prop, and `validate()` blocks
             send when any required group is empty. */}
        {(overridableSources.length > 0 || senderTypedGroups.length > 0) && (
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t("signatures.newDoc.documentValuesTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {t("signatures.newDoc.documentValuesHelper")}
            </Typography>
            <Stack spacing={1.5}>
              {overridableSources.map(({ source, sampleField, required }) => {
                const friendly =
                  getFieldFriendlyName(source) ||
                  getFieldFriendlyName(sampleField.labelKey) ||
                  source;
                const resolved = resolveSourceValue(sampleField);
                const overrideVal = fieldOverrides[source] ?? "";
                const isMissing =
                  !resolved &&
                  !overrideVal &&
                  missingPrefillFields.some((m) => m.fieldName === sampleField.name);
                return (
                  <SignatureFormFieldInput
                    key={`override:${source}`}
                    field={sampleField}
                    value={overrideVal}
                    onChange={(next) =>
                      setFieldOverrides((prev) => {
                        const updated = { ...prev };
                        if (next === "") delete updated[source];
                        else updated[source] = next;
                        return updated;
                      })
                    }
                    label={friendly}
                    placeholder={resolved}
                    // Asterisk semantics: "you must fill this before send".
                    // True when the underlying schema marks any field with
                    // this source as required, OR when the source can't
                    // resolve from the current context (missing-fields
                    // warning would gate send anyway).
                    required={required || isMissing}
                    isMissing={isMissing}
                    missingHelperText={
                      t("signatures.newDoc.realtorFieldRequired") as string
                    }
                  />
                );
              })}
              {senderTypedGroups.map(({ key, field: f, required }) => (
                <SignatureFormFieldInput
                  key={`typed:${key}`}
                  field={f}
                  value={fieldValues[key] || ""}
                  onChange={(next) =>
                    setFieldValues((prev) => ({ ...prev, [key]: next }))
                  }
                  label={
                    f.type === "price" &&
                    (!f.name || DEFAULT_PRICE_FIELD_NAMES.has(f.name.trim()))
                      ? (t("signatures.newDoc.priceFieldLabel") as string)
                      : getFieldFriendlyName(f.prefillSource) ||
                        getFieldFriendlyName(f.labelKey) ||
                        f.name
                  }
                  required={required}
                />
              ))}
            </Stack>
          </Card>
        )}

        {/* Signer-prefill card — typed fields the SIGNER fills during the
            ceremony, which the realtor may optionally pre-fill here.
            Contact-sourced groups (name / email / T.Z. / address) auto-
            populate from the picked contact; the rest start empty. A filled
            group grows an "allow the customer to edit" checkbox (on by
            default): checked → the value seeds the signer's input (they can
            change it); unchecked → the value is baked into the document and
            the signer gets no input for it. Never required — an empty input
            simply isn't seeded, and the field stays a normal signer field.
            Placed right below the "values stamped into the document" card so
            all value-entry surfaces sit together. */}
        {!manualSignerMode && visibleSignerPrefillGroups.length > 0 && (
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t("signatures.newSignatureForm.signerPrefillTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {t("signatures.newSignatureForm.signerPrefillHelper")}
            </Typography>
            <SignerPrefillInputs
              groups={visibleSignerPrefillGroups}
              values={signerPrefillValues}
              locked={signerPrefillLocked}
              onValueChange={(key, value) => setSignerPrefillValues((prev) => ({ ...prev, [key]: value }))}
              onLockChange={(key, value) => setSignerPrefillLocked((prev) => ({ ...prev, [key]: value }))}
            />
          </Card>
        )}

        {/* Additional terms — send-time clauses appended into the template's
            terms block (title + rich text). Rendered into the document and
            visible to the signer, unlike the internal notes below. */}
        {hasAdditionalTermsBlock && (
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t("signatures.additionalTerms.sectionTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {t("signatures.additionalTerms.sectionHelper")}
            </Typography>
            <AdditionalTermsInput
              title={termsTitle}
              onTitleChange={setTermsTitle}
              onTermsChange={(html, isEmpty) => {
                setTermsHtml(html);
                setTermsEmpty(isEmpty);
              }}
              // Edit-after-send prefill: the terms typed at the original send
              // (from the persisted snapshot). The prefill effect seeds the
              // matching parent state (termsHtml / termsEmpty).
              initialContent={editDocument?.sendFormSnapshot?.additionalTerms?.html}
            />
          </Card>
        )}

        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("signatures.newSignatureForm.notesLabel")}
          </Typography>
          <TextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder={t("signatures.newSignatureForm.notesPlaceholder") as string}
          />
        </Card>

      {/* OTP enforcement — per-send policy. Unchecking weakens the identity
          proof (link possession only), so the off state renders with a
          warning tint + explicit helper, mirroring the hidden-addresses
          toggle's visual language. */}
      <Box
        sx={{
          p: 1.25,
          borderRadius: 1,
          border: "1px solid",
          borderColor: enforceOtp ? "divider" : "warning.light",
          bgcolor: enforceOtp ? "transparent" : "rgba(255,193,7,0.08)"
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={enforceOtp}
              onChange={(e) => setEnforceOtp(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2">
                {t("signatures.newSignatureForm.enforceOtpLabel")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {enforceOtp
                  ? t("signatures.newSignatureForm.enforceOtpHelperOn")
                  : t("signatures.newSignatureForm.enforceOtpHelperOff")}
              </Typography>
            </Box>
          }
        />
      </Box>

      {/* Document number(s) — the caller's next per-user number, editable per
          document for this send. Kept as the LAST field in the form (by
          request) for BOTH modes: single-template shows one field; bundle
          mode shows one compact input per member with the template's name
          beside it. Hidden entirely when the counter fetch FAILED — no field
          beats a fake "1". Skeletons while the fetch is in flight. */}
      {selectedTemplate && (selectedBundle ? bundleCountersState !== "error" : counterFetchState !== "error") && (
        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("signatures.newSignatureForm.documentNumberLabel")}
          </Typography>
          {selectedBundle ? (
            <>
              <Stack spacing={1.25}>
                {bundleTemplates.map((tp) => (
                  <Stack key={tp.id} direction="row" alignItems="center" spacing={1.5}>
                    {bundleCountersState === "loading" ? (
                      <Skeleton
                        variant="rectangular"
                        width={110}
                        height={40}
                        sx={{ borderRadius: 1, flexShrink: 0 }}
                      />
                    ) : (
                      <TextField
                        size="small"
                        value={bundleNumberInputs[tp.id] ?? ""}
                        onChange={(e) =>
                          setBundleNumberInputs((prev) => ({
                            ...prev,
                            [tp.id]: e.target.value.replace(/[^\d]/g, "")
                          }))
                        }
                        placeholder={String(bundleCounters[tp.id]?.nextDocumentNumber ?? 1)}
                        inputProps={{ inputMode: "numeric", dir: "ltr", style: { textAlign: "center" } }}
                        InputProps={
                          bundleCounters[tp.id]?.numberPrefix
                            ? {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {bundleCounters[tp.id]?.numberPrefix}
                                  </InputAdornment>
                                )
                              }
                            : undefined
                        }
                        error={isBundleMemberNumberInvalid(tp.id)}
                        sx={{ width: 110, flexShrink: 0 }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 0, overflowWrap: "break-word" }}
                    >
                      <TemplateNameTag nameTag={tp.nameTag} />
                      {tp.name}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Typography
                variant="caption"
                color={anyBundleNumberInvalid ? "error" : "text.secondary"}
                sx={{ mt: 1, display: "block" }}
              >
                {anyBundleNumberInvalid
                  ? t("signatures.newSignatureForm.documentNumberInvalid")
                  : t("signatures.newSignatureForm.bundleDocNumbersHelper")}
              </Typography>
            </>
          ) : editDocument ? (
            // EDIT MODE: the number was allocated at the original send and is
            // part of the document's identity — display it, never re-allocate.
            <TextField
              size="small"
              value={editDocument.formattedNumber}
              disabled
              inputProps={{ dir: "ltr" }}
              helperText={t("signatures.newSignatureForm.documentNumberEditLocked") as string}
              sx={{ maxWidth: 280 }}
            />
          ) : counterFetchState === "loading" ? (
            <Skeleton variant="rectangular" width={280} height={40} sx={{ borderRadius: 1 }} />
          ) : (
            <TextField
              size="small"
              value={documentNumberInput}
              onChange={(e) => setDocumentNumberInput(e.target.value.replace(/[^\d]/g, ""))}
              placeholder={String(selectedTemplateUserCounter.nextDocumentNumber)}
              inputProps={{ inputMode: "numeric", dir: "ltr" }}
              InputProps={
                selectedTemplateUserCounter.numberPrefix
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          {selectedTemplateUserCounter.numberPrefix}
                        </InputAdornment>
                      )
                    }
                  : undefined
              }
              error={documentNumberInvalid}
              helperText={
                documentNumberInvalid
                  ? (t("signatures.newSignatureForm.documentNumberInvalid") as string)
                  : overrideDocumentNumber !== null &&
                    overrideDocumentNumber !== selectedTemplateUserCounter.nextDocumentNumber
                  ? (t("signatures.newSignatureForm.documentNumberOverrideHelper", {
                      next: selectedTemplateUserCounter.nextDocumentNumber
                    }) as string)
                  : (t("signatures.newSignatureForm.documentNumberHelper") as string)
              }
              sx={{ maxWidth: 280 }}
            />
          )}
        </Card>
      )}

        <Divider />

        {/* Hard-block: missing realtor signature image. Cannot be bypassed —
            shipping a "signed" document with no signature stamp is never
            right. The "open settings" CTA only makes sense when the missing
            user is the logged-in user; when an admin picked someone else,
            that user has to upload their own signature. */}
        {hasBlockingMissing && (() => {
          const isPickedUserMissing =
            !!pickedUserEmail && pickedUserEmail !== (userSettings as any)?.id;
          return (
            <StackedAlert
              severity="error"
              title={t("signatures.newDoc.missingSignatureBlockTitle") as string}
              action={
                !isPickedUserMissing ? (
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => navigate(`${PATH_DASHBOARD.general.settings}/media`)}
                  >
                    {t("signatures.newDoc.missingSignatureBlockCta")}
                  </Button>
                ) : null
              }
            >
              <Typography variant="caption">
                {isPickedUserMissing
                  ? t("signatures.newDoc.missingSignatureBlockHelperPickedUser", {
                      email: pickedUserEmail
                    })
                  : t("signatures.newDoc.missingSignatureBlockHelper")}
              </Typography>
            </StackedAlert>
          );
        })()}

        {/* Soft block: any value the document needs but doesn't yet have —
            unresolved prefill sources AND sender-typed required-empty
            groups. Send is gated until the realtor either fills them in
            or explicitly acknowledges via the bypass checkbox. */}
        {allMissingFields.length > 0 && !hasBlockingMissing && (
          <StackedAlert
            severity="warning"
            title={t("signatures.newDoc.missingFieldsTitle") as string}
          >
            <Typography variant="caption">
              {t("signatures.newDoc.missingFieldsHelper")}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {allMissingFields.map((m, idx) => (
                // List is deduped per-source (or per user_info_block
                // sub-line, or per sender-typed linkGroup), so we show
                // the friendly label only — surfacing the first field's
                // internal name was misleading when several fields shared
                // the source / group.
                <li key={`${m.fieldName}-${m.userKey ?? idx}`}>
                  <Typography variant="body2">{m.label}</Typography>
                </li>
              ))}
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={bypassMissingFields}
                  onChange={(e) => setBypassMissingFields(e.target.checked)}
                />
              }
              label={t("signatures.newDoc.bypassMissingFields") as string}
              sx={{ m: 0, alignSelf: "flex-start" }}
            />
          </StackedAlert>
        )}

        {/* Hard block, beside the button it disables: no "send anyway" for a
            questionnaire without its property's address. */}
        <QuestionnaireAddressAlert show={questionnaireAddressMissing} listedFor={questionnaireListedFor} />

        {optionalSignerFields.length > 0 && (
          <StackedAlert
            severity="info"
            title={t("signatures.newDoc.optionalSignerFieldsTitle") as string}
          >
            <Typography variant="caption">
              {t("signatures.newDoc.optionalSignerFieldsHelper")}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {optionalSignerFields.map((f) => (
                <li key={f.key}>
                  <Typography variant="body2">{f.label}</Typography>
                </li>
              ))}
            </Box>
          </StackedAlert>
        )}

      </Box>

      {/* The wait, right above the button that started it — outside the
          scroll area, so it is on screen at every width without scrolling. */}
      {progress && <SignaturePreparationProgress progress={progress} questionnaire={questionnaireOnlySelected} />}

      {/* Why "הכנת חוזה" waits for the role's documents: one still loading, or
          one that could not be loaded ("נסו שוב", and for the exclusivity order
          "המשך בלי בלעדיות", which unticks the box). Same place as the wait above. */}
      <RoleDocumentsNote
        block={roleState.block}
        onRetry={retryRoleDocuments}
        onWithoutExclusivity={() => setWithExclusivity(false)}
      />

      {/* Action footer — a real non-scrolling bar OUTSIDE the scroll area
          above (a flex sibling, not `position: sticky` inside it), so form
          content can never render under or through it. The bottom padding
          accounts for the iPhone home-indicator safe area when the dialog is
          fullscreen. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          px: { xs: 2, md: 3 },
          py: 1.5,
          pb: "calc(12px + env(safe-area-inset-bottom))"
        }}
      >
        <Button
          startIcon={<PreviewIcon />}
          onClick={() => setPreviewing(true)}
          disabled={sending || !previewHtml}
        >
          {t("signatures.newSignatureForm.previewButton")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="primary"
          // The send arrow sits at the label's END and points the way the line reads (mirrored in
          // RTL), like every send button. Keep this branch shape: this render is big enough that
          // eslint-plugin-react-hooks 4.2's path counter overflows when a branch is added or removed
          // here, and then reports every hook in the form as "called conditionally".
          endIcon={
            sending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon sx={isRtl ? { transform: "scaleX(-1)" } : undefined} />
            )
          }
          onClick={onSend}
          disabled={sending || !!validate()}
        >
          {/* A questionnaire on its own is prepared as one: "הכנת שאלון", not "הכנת חוזה". */}
          {editDocument
            ? t("signatures.newSignatureForm.editResendButton", { context: questionnaireOnlySelected ? "questionnaire" : undefined })
            : t("signatures.newSignatureForm.sendButton", { context: questionnaireOnlySelected ? "questionnaire" : undefined })}
        </Button>
      </Stack>

      <DocumentPreviewDialog
        open={previewing}
        onClose={() => setPreviewing(false)}
        html={previewHtml || ""}
        bundleDocs={bundlePreviewDocs}
        prefilledValues={nonEmptySignerPrefills}
      />
    </Box>
  );
}

// Re-exports preserved for any importers expecting the legacy surface.
export { EditIcon };
